import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Drawer, Typography, Box, IconButton, Paper, Card, CardContent, Chip, TextField, Button, Accordion, AccordionSummary, AccordionDetails, Alert, CircularProgress, Snackbar, Tooltip, Autocomplete, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions } from '@mui/material';
import { alpha, keyframes } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';

import { MapContainer, TileLayer, Marker, Popup, Polyline, useMap, useMapEvents, LayerGroup, Circle, CircleMarker, Pane } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { v4 as uuidv4 } from 'uuid';
import { AppContext, Line, Layer, Point } from '../../../context/context';
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip as ChartTooltip, Legend } from 'chart.js';
import TripsPerHourChart from './simulation-results/TripsPerHourChart';
import ResultsMapLayers from './simulation-results/ResultsMapLayers';
import {
  drawerPrimaryActionSx,
  drawerSecondaryActionSx,
} from '../../../theme/componentStyles';
import {
  binRequestsPerHour,
  buildUnmatchedRideGoogleMapsDrivingUrl,
  coerceRideCoordinate,
  getRidePointsTimeRange,
  parseRequestTimeToMs,
  roadRouteRequestTimesMs,
  unmatchedRideStableKey,
} from './simulation-results/utils';
import MetricsGrid from './simulation-results/MetricsGrid';
import SimulationResultsSidebar from './simulation-results/SimulationResultsSidebar';
import LiftangoInsightsCarousel from './simulation-results/LiftangoInsightsCarousel';
import AnalyticsDeck, { ANALYTICS_DECK_CHROME_HEIGHT, ANALYTICS_DECK_CONTENT_HEIGHT_DEFAULT } from './simulation-results/AnalyticsDeck';
import { TRIP_RENDER_CAP } from './TripLayerRenderer';
import MapPointPopupContent from './simulation-results/MapPointPopupContent';
import { SimulationResultsTooltip } from './simulation-results/SimulationResultsTooltip';
import {
  QuickCopyButton,
  UNMATCHED_RIDE_TOOLTIP_THEME,
  unmatchedRideTooltipPopperSx,
  UNMATCHED_RIDE_IDENTIFIER_COLUMNS,
} from './simulation-results/UnmatchedRideDetails';
import {
  collectActivityIdCandidatesForTripFocus,
  collectRidePointIdCandidatesForTripFocus,
  passengerUserIdFromActivitiesAndRidePoint,
  resolveTripTravelDisplay,
  TRAVEL_TIME_TOOLTIP_MISSING,
} from '../../../utils/simulation/tripFocusFromActivities';
import {
  activityMatchesTripKeys,
  haversineDistanceMeters,
  mergeRoadRoutesForTripGradient,
  toFiniteNumber,
  vehicleRouteMatchesTrip,
  type AddressPairRoute,
} from '../../../utils/simulation/tripRouteMatching';
import { activityStopKindForUi } from '../../../utils/simulation/inferActivityStopKind';
import {
  useVehicleOperations,
  DEFAULT_CO2_RATE_KG_PER_KM,
  getVehicleKey,
  type VehicleOperation,
} from './simulation-results/useVehicleOperations';
import { resolveLegAddress } from './simulation-results/utils/addressResolution';
import { useSimulation } from '../../../context/SimulationContext';
import { useSimulationResultsContext } from '../../../context/SimulationResultsContext';
import { useFixedRoutes } from '../../../context/FixedRoutesContext';
import SimulationStepTrackerPanel from './SimulationStepTrackerPanel';
import { SimulationRunSummary, SimulationActivity, type UnmatchedRide } from '../../../types/simulation';
import type { Fleet } from './FleetMenu';
import DriverActivityDetailPanel from './simulation-results/DriverActivityDetailPanel';
import { getActivityCoordinates } from './simulation-results/DriverActivityDetailPanel';
import FrodoReportComponent from '../../simulation/FrodoReport';
import {
  fetchSimulationMetrics,
  fetchSimulationActivities,
  fetchSolutionResults,
  buildRidePointsFromActivities,
  fetchUnmatchedRides,
  fetchSimulationVehicles,
  fetchSimulationSettings,
} from '../../../services/simulation/resultsApi';
import { shareSimulationResults, listSimulationResultsShares, revokeSimulationResultsShare } from '../../../services/simulation/resultsApi';
import { simulationService } from '../../../services/simulation/simulationService';
import {
  fetchRoadRoutesByResultsIdWithMetadata,
  type GeoJsonLineString,
  type RoadRouteWithMetadata,
  type SegmentRouteMetadata,
} from '../../../services/simulation/resultsPathsService';
import { transformSimulationResultsToFixedRoutes } from '../../../services/simulation/simulationResultsToFixedRoutes';
import { transformResultsVehiclesToFleet, mergeImportedFleet } from '../../../services/simulation/resultsVehiclesToFleet';
import { stampShiftNamesOnActivities } from '../../../services/simulation/shiftLabelRegistry';
import { trackAnalyticsEvent } from '../../../services/analytics';
import { ANALYTICS_EVENT_NAMES } from '../../../services/eventTaxonomy';
import ShareAccessDialog from '../../sharing/ShareAccessDialog';
import { Icon } from '@liftango/ui-icons';
import { ICON_FALLBACKS } from '../../../utils/iconFallbacks';
import { opsColors, opsElevation } from '../../../theme/opsTokens';
import GoogleTileLayer from '../GoogleTileLayer';
import { useGoogleBasemap } from '../../../services/googleTiles/useGoogleBasemap';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, ChartTooltip, Legend);

type StepStatus = 'notStarted' | 'inProgress' | 'success' | 'failed';
function filterOutScenarioStep(statuses: StepStatus[]): StepStatus[] {
  if (statuses.length <= 5) return statuses;
  return [...statuses.slice(0, 4), ...statuses.slice(5)];
}

function runTimeMsForSort(run: SimulationRunSummary): number {
  if (run.run_time == null || run.run_time === '') return 0;
  const t = Date.parse(run.run_time);
  return Number.isFinite(t) ? t : 0;
}

/** Newest-first ordering for the run selector (matches refreshed API list behavior). */
function sortSimulationRunsNewestFirst(runs: SimulationRunSummary[]): SimulationRunSummary[] {
  return [...runs].sort((a, b) => {
    const diff = runTimeMsForSort(b) - runTimeMsForSort(a);
    if (diff !== 0) return diff;
    return b.run_id.localeCompare(a.run_id);
  });
}

/**
 * Combine fetched/local results with in-session history so just-finished runs appear immediately,
 * ordered by recency (not by merge-append order).
 */
function mergeResultsWithHistoryForSelect(
  results: SimulationRunSummary[],
  history: SimulationRunSummary[]
): SimulationRunSummary[] {
  const byId = new Map<string, SimulationRunSummary>();
  for (const s of results) byId.set(s.run_id, s);
  for (const h of history) {
    if (!byId.has(h.run_id)) byId.set(h.run_id, h);
  }
  return sortSimulationRunsNewestFirst(Array.from(byId.values()));
}

function normalizeImportedRideRequestTime(raw: unknown): string {
  if (typeof raw !== 'string') return '';
  const trimmed = raw.trim();
  if (!trimmed) return '';
  return trimmed.replace(/(Z|[+-]\d{2}:\d{2})$/i, '');
}

function normalizeImportedRideRequestType(raw: unknown): 'departure_fixed' | 'arrival_fixed' {
  if (typeof raw !== 'string') return 'departure_fixed';
  const value = raw.trim().toLowerCase();
  return value === 'arrival_fixed' ? 'arrival_fixed' : 'departure_fixed';
}

const simMapLoaderFadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const readyButtonPulse = keyframes`
  0% {
    transform: scale(1);
    box-shadow: 0 0 0 0 rgba(46, 125, 50, 0.55);
  }
  70% {
    transform: scale(1.03);
    box-shadow: 0 0 0 10px rgba(46, 125, 50, 0);
  }
  100% {
    transform: scale(1);
    box-shadow: 0 0 0 0 rgba(46, 125, 50, 0);
  }
`;

/**
 * Fits the Leaflet map to the bounding box of the supplied points.
 * Only re-fits when `fitKey` changes (e.g. the selected run ID), so scrubbing
 * the timeline (which changes filtered pointsToShow every tick) never triggers
 * a new zoom animation â€” preventing the _leaflet_pos race condition where
 * _onZoomTransitionEnd fires after the map pane has been removed from the DOM.
 */
function FitBounds({ points, fitKey }: { points: any[]; fitKey: string }) {
  const map = useMap();
  const lastFitKey = React.useRef<string | null>(null);

  React.useEffect(() => {
    if (fitKey === lastFitKey.current || points.length === 0) return;
    lastFitKey.current = fitKey;
    try {
      map.stop(); // cancel any in-progress animation before starting a new one
      const latlngs = points
        .filter((p) => typeof p.from_latitude === 'number' && typeof p.from_longitude === 'number')
        .map((p) => [p.from_latitude, p.from_longitude] as [number, number]);
      if (latlngs.length === 0) return;
      const bounds = L.latLngBounds(latlngs);
      if (bounds.isValid()) {
        map.fitBounds(bounds, { padding: [20, 20], animate: false });
      }
    } catch {
      // map pane may have already been removed; nothing to do
    }
  }, [fitKey, points, map]);

  return null;
}

function ClearTripFocusOnMapClick({
  tripFocusActive,
  onClearTripFocus,
}: {
  tripFocusActive: boolean;
  onClearTripFocus: () => void;
}) {
  useMapEvents({
    click: (event) => {
      if (!tripFocusActive) return;
      const target = event.originalEvent?.target as HTMLElement | null;
      const clickedInteractive =
        target?.closest(
          '.leaflet-marker-icon, .leaflet-popup, .leaflet-control, .leaflet-interactive, .sim-map-point-popup'
        ) != null;
      if (!clickedInteractive) {
        onClearTripFocus();
      }
    },
  });
  return null;
}

/** When highlightedActivity is set, centers the map on it and shows a highlight circle (page variant only). */
function HighlightActivityMapEffect({
  highlightedActivity,
}: {
  highlightedActivity: { lat: number; lng: number; activityId?: string } | null;
}) {
  const map = useMap();
  React.useEffect(() => {
    if (!highlightedActivity) return;
    const zoom = map.getZoom();
    map.setView([highlightedActivity.lat, highlightedActivity.lng], Number.isFinite(zoom) ? zoom : 16);
  }, [highlightedActivity, map]);
  if (!highlightedActivity) return null;
  return (
    <Circle
      center={[highlightedActivity.lat, highlightedActivity.lng]}
      radius={80}
      pathOptions={{
        color: '#EE7334',
        fillColor: '#EE7334',
        fillOpacity: 0.25,
        weight: 3,
      }}
    />
  );
}

export interface SimulationResultsMenuProps {
  open: boolean;
  onClose: () => void;
  authToken?: string; // Optional auth token for API calls
  /** When 'page', render as full page (e.g. /simulation-results); when 'drawer', render as sidebar drawer (default) */
  variant?: 'drawer' | 'page';
}

const buildMetrics = (t: (key: string) => string) => [
  { label: t('simResultsMenu.metrics.utilization'), field: 'passenger_per_vehicle_hour', icon: <Icon size={20} name="chart-bar" />, unit: '' },
  { label: t('simResultsMenu.metrics.maxGroups'), field: 'max_pax', icon: <Icon size={20} name="users" />, unit: '' },
  { label: t('simResultsMenu.metrics.availableVehicles'), field: 'no_vehicles', icon: <Icon size={20} name="car-side" />, unit: '' },
  { label: t('simResultsMenu.metrics.allocatedVehicles'), field: 'allocated_vehicle_shifts', icon: <Icon size={20} d={ICON_FALLBACKS.AssignmentTurnedIn} />, unit: '' },
  { label: t('simResultsMenu.metrics.vehicleHours'), field: 'vehicle_hours', icon: <Icon size={20} name="clock" />, unit: '' },
  { label: t('simResultsMenu.metrics.sharedTripRate'), field: 'pcnt_shared_rides', icon: <Icon size={20} d={ICON_FALLBACKS.Share} />, unit: '%' },
  { label: t('simResultsMenu.metrics.vehicleDistance'), field: 'total_km_travelled', icon: <Icon size={20} name="route" />, unit: 'km' },
  { label: t('simResultsMenu.metrics.firstQuartileWait'), field: 'waiting_time_min_first_quartile', icon: <Icon size={20} d={ICON_FALLBACKS.Timer} />, unit: 'min' },
  { label: t('simResultsMenu.metrics.medianWait'), field: 'waiting_time_min_median', icon: <Icon size={20} d={ICON_FALLBACKS.Timer} />, unit: 'min' },
  { label: t('simResultsMenu.metrics.activeKm'), field: 'active_km', icon: <Icon size={20} name="car-side" />, unit: 'km' },
  { label: t('simResultsMenu.metrics.idleKm'), field: 'dead_running_km', icon: <Icon size={20} name="car-side" />, unit: 'km' },
  { label: t('simResultsMenu.metrics.medianTripDistance'), field: 'trip_distance_median', icon: <Icon size={20} d={ICON_FALLBACKS.Straighten} />, unit: 'km' },
  { label: t('simResultsMenu.metrics.medianTripTime'), field: 'median_time_on_vehicle', icon: <Icon size={20} name="clock" />, unit: 'min' },
  { label: t('simResultsMenu.metrics.maxTripTime'), field: 'max_time_on_vehicle', icon: <Icon size={20} d={ICON_FALLBACKS.TimerOff} />, unit: 'min' },
];

const extractMetricsRow = (payload: any): Partial<SimulationRunSummary> | null => {
  if (!payload) return null;
  if (Array.isArray(payload)) {
    return payload[0] || null;
  }
  if (Array.isArray(payload.data)) {
    return payload.data[0] || null;
  }
  if (payload.data && typeof payload.data === 'object') {
    return payload.data;
  }
  return typeof payload === 'object' ? payload : null;
};

const isVehicleVisible = (vehicleId: string | null | undefined, visibilityMap: Record<string, boolean>): boolean => {
  if (!vehicleId) return true;
  return visibilityMap[vehicleId] !== false;
};

const resolveRidePointVehicleId = (
  ridePoint: any,
  knownVehicleIds: string[],
): string | null => {
  if (typeof ridePoint?.vehicle_id === 'string' && ridePoint.vehicle_id.trim()) {
    return ridePoint.vehicle_id;
  }
  const rideId = typeof ridePoint?.ride_id === 'string' ? ridePoint.ride_id : '';
  if (!rideId) return null;
  const sortedIds = [...knownVehicleIds].sort((a, b) => b.length - a.length);
  const matched = sortedIds.find((vehicleId) => rideId.startsWith(`${vehicleId}-`));
  return matched ?? null;
};

export const filterRidePointsByVisibility = (
  ridePoints: any[],
  visibilityMap: Record<string, boolean>,
  knownVehicleIds: string[],
): any[] =>
  ridePoints.filter((ridePoint) => isVehicleVisible(resolveRidePointVehicleId(ridePoint, knownVehicleIds), visibilityMap));

// Fast-path variant: accepts an already-sorted (longest-first) array to skip the per-call sort.
const resolveRidePointVehicleIdFast = (
  ridePoint: any,
  sortedIds: string[],
): string | null => {
  if (typeof ridePoint?.vehicle_id === 'string' && ridePoint.vehicle_id.trim()) {
    return ridePoint.vehicle_id;
  }
  const rideId = typeof ridePoint?.ride_id === 'string' ? ridePoint.ride_id : '';
  if (!rideId) return null;
  return sortedIds.find((vehicleId) => rideId.startsWith(`${vehicleId}-`)) ?? null;
};

export const filterRidePointsByVisibilityFast = (
  ridePoints: any[],
  visibilityMap: Record<string, boolean>,
  sortedIds: string[],
): any[] =>
  ridePoints.filter((ridePoint) =>
    isVehicleVisible(resolveRidePointVehicleIdFast(ridePoint, sortedIds), visibilityMap)
  );

export const filterActivitiesByVisibility = (
  activities: SimulationActivity[],
  visibilityMap: Record<string, boolean>,
): SimulationActivity[] =>
  activities.filter((activity) => isVehicleVisible(getVehicleKey(activity), visibilityMap));

// Match Operations screen: green circle + up arrow, orange circle + down arrow. Use SVG arrows (not triangles).
const OP_GREEN = '#5FC98F';
const OP_ORANGE = '#EE7334';
const OP_GREY = '#7C8594';
const MAP_NODE_SIZE = 26;
const arrowUpSvg = '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M12 19V5M5 12l7-7 7 7"/></svg>';
const arrowDownSvg = '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M12 5v14M5 12l7 7 7-7"/></svg>';
const busSvg = '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><rect x="4" y="3" width="16" height="14" rx="2"/><path d="M4 10h16"/><path d="M8 17v3M16 17v3"/><circle cx="8" cy="20" r="1.2" fill="white"/><circle cx="16" cy="20" r="1.2" fill="white"/></svg>';
// Canvas dots: the preview draws every ride.
const RIDE_PICKUP_DOT = { color: '#fff', weight: 1.5, fillColor: OP_GREEN, fillOpacity: 1 };
const RIDE_DROPOFF_DOT = { color: '#fff', weight: 1.5, fillColor: OP_ORANGE, fillOpacity: 1 };
const MUTED_MARKER_GREY = '#B0BAC8';
/** Muted markers when another trip is focused (Leaflet markers often ignore the React `opacity` prop). */
const pickupIconMuted = L.divIcon({
  className: 'sim-pickup-marker sim-marker-muted',
  html: `<div style="
    width:${MAP_NODE_SIZE}px;height:${MAP_NODE_SIZE}px;border-radius:50%;opacity:0.55;
    background:${MUTED_MARKER_GREY};border:2px solid #fff;box-shadow:0 1px 4px rgba(0,0,0,0.15);
    display:flex;align-items:center;justify-content:center;
  " title="Pick-up">${arrowUpSvg}</div>`,
  iconSize: [MAP_NODE_SIZE, MAP_NODE_SIZE],
  iconAnchor: [MAP_NODE_SIZE / 2, MAP_NODE_SIZE / 2],
  popupAnchor: [0, -MAP_NODE_SIZE / 2],
});
const dropoffIconMuted = L.divIcon({
  className: 'sim-dropoff-marker sim-marker-muted',
  html: `<div style="
    width:${MAP_NODE_SIZE}px;height:${MAP_NODE_SIZE}px;border-radius:50%;opacity:0.55;
    background:${MUTED_MARKER_GREY};border:2px solid #fff;box-shadow:0 1px 4px rgba(0,0,0,0.15);
    display:flex;align-items:center;justify-content:center;
  " title="Drop-off">${arrowDownSvg}</div>`,
  iconSize: [MAP_NODE_SIZE, MAP_NODE_SIZE],
  iconAnchor: [MAP_NODE_SIZE / 2, MAP_NODE_SIZE / 2],
  popupAnchor: [0, -MAP_NODE_SIZE / 2],
});
const depotIcon = L.divIcon({
  className: 'sim-depot-marker',
  html: `<div style="
    width:${MAP_NODE_SIZE}px;height:${MAP_NODE_SIZE}px;border-radius:50%;
    background:${OP_GREY};border:2px solid #fff;box-shadow:0 1px 4px rgba(0,0,0,0.25);
    display:flex;align-items:center;justify-content:center;
  " title="Depot">${busSvg}</div>`,
  iconSize: [MAP_NODE_SIZE, MAP_NODE_SIZE],
  iconAnchor: [MAP_NODE_SIZE / 2, MAP_NODE_SIZE / 2],
  popupAnchor: [0, -MAP_NODE_SIZE / 2],
});
const depotIconMuted = L.divIcon({
  className: 'sim-depot-marker sim-marker-muted',
  html: `<div style="
    width:${MAP_NODE_SIZE}px;height:${MAP_NODE_SIZE}px;border-radius:50%;opacity:0.36;
    background:${OP_GREY};border:2px solid #fff;box-shadow:0 1px 4px rgba(0,0,0,0.2);
    display:flex;align-items:center;justify-content:center;
  " title="Depot">${busSvg}</div>`,
  iconSize: [MAP_NODE_SIZE, MAP_NODE_SIZE],
  iconAnchor: [MAP_NODE_SIZE / 2, MAP_NODE_SIZE / 2],
  popupAnchor: [0, -MAP_NODE_SIZE / 2],
});
/** Dark grey pickup/dropoff for unallocated rides â€” clearly not the allocated green/orange stops. */
const UNMATCHED_STOP_GREY = '#374151';
const unmatchedPickupIcon = L.divIcon({
  className: 'sim-unmatched-pickup-marker',
  html: `<div style="
    width:${MAP_NODE_SIZE}px;height:${MAP_NODE_SIZE}px;border-radius:50%;
    background:${UNMATCHED_STOP_GREY};border:2px solid #fff;box-shadow:0 1px 4px rgba(0,0,0,0.3);
    display:flex;align-items:center;justify-content:center;
  " title="Unallocated pick-up">${arrowUpSvg}</div>`,
  iconSize: [MAP_NODE_SIZE, MAP_NODE_SIZE],
  iconAnchor: [MAP_NODE_SIZE / 2, MAP_NODE_SIZE / 2],
  popupAnchor: [0, -MAP_NODE_SIZE / 2],
});
const unmatchedDropoffIcon = L.divIcon({
  className: 'sim-unmatched-dropoff-marker',
  html: `<div style="
    width:${MAP_NODE_SIZE}px;height:${MAP_NODE_SIZE}px;border-radius:50%;
    background:${UNMATCHED_STOP_GREY};border:2px solid #fff;box-shadow:0 1px 4px rgba(0,0,0,0.3);
    display:flex;align-items:center;justify-content:center;
  " title="Unallocated drop-off">${arrowDownSvg}</div>`,
  iconSize: [MAP_NODE_SIZE, MAP_NODE_SIZE],
  iconAnchor: [MAP_NODE_SIZE / 2, MAP_NODE_SIZE / 2],
  popupAnchor: [0, -MAP_NODE_SIZE / 2],
});
const unmatchedPickupIconMuted = L.divIcon({
  className: 'sim-unmatched-pickup-marker sim-marker-muted',
  html: `<div style="
    width:${MAP_NODE_SIZE}px;height:${MAP_NODE_SIZE}px;border-radius:50%;opacity:0.32;
    background:${UNMATCHED_STOP_GREY};border:2px solid #fff;box-shadow:0 1px 4px rgba(0,0,0,0.2);
    display:flex;align-items:center;justify-content:center;
  " title="Unallocated pick-up">${arrowUpSvg}</div>`,
  iconSize: [MAP_NODE_SIZE, MAP_NODE_SIZE],
  iconAnchor: [MAP_NODE_SIZE / 2, MAP_NODE_SIZE / 2],
  popupAnchor: [0, -MAP_NODE_SIZE / 2],
});
const unmatchedDropoffIconMuted = L.divIcon({
  className: 'sim-unmatched-dropoff-marker sim-marker-muted',
  html: `<div style="
    width:${MAP_NODE_SIZE}px;height:${MAP_NODE_SIZE}px;border-radius:50%;opacity:0.32;
    background:${UNMATCHED_STOP_GREY};border:2px solid #fff;box-shadow:0 1px 4px rgba(0,0,0,0.2);
    display:flex;align-items:center;justify-content:center;
  " title="Unallocated drop-off">${arrowDownSvg}</div>`,
  iconSize: [MAP_NODE_SIZE, MAP_NODE_SIZE],
  iconAnchor: [MAP_NODE_SIZE / 2, MAP_NODE_SIZE / 2],
  popupAnchor: [0, -MAP_NODE_SIZE / 2],
});

type RoadRouteByRideId = Record<string, RoadRouteWithMetadata>;
type ShiftGeometrySeed = Record<string, [number, number][][]>;
type ShiftRouteMetadataSeed = Record<string, SegmentRouteMetadata[]>;
const RIDE_ROUTE_KEY_FIELDS = [
  'ride_id',
  'request_id',
  'driver_ride_id',
  'rider_ride_id',
  'external_reference_id',
  'id',
] as const;

function buildShiftGeometrySeedFromRoadRoutes(routesById: RoadRouteByRideId): ShiftGeometrySeed {
  type SeedRow = {
    shiftId: string;
    segmentIndex: number;
    fromActivity: number;
    toActivity: number;
    routeId: string;
    geometry: [number, number][];
  };
  const rows: SeedRow[] = [];
  const seen = new Set<string>();

  Object.entries(routesById).forEach(([routeId, route]) => {
    const geometry = route?.geometry;
    const metadata = route?.metadata;
    const shiftId = metadata?.vehicle_shift_id?.trim();
    if (!geometry || geometry.type !== 'LineString' || !shiftId || !Array.isArray(geometry.coordinates)) return;

    const latLng = geometry.coordinates
      .map(([lng, lat]) => [lat, lng] as [number, number])
      .filter((point) => Number.isFinite(point[0]) && Number.isFinite(point[1]));
    if (latLng.length < 2) return;

    const segmentIndex =
      typeof metadata?.segment_index === 'number' && Number.isFinite(metadata.segment_index)
        ? metadata.segment_index
        : Number.MAX_SAFE_INTEGER;
    const fromActivity =
      typeof metadata?.from_driver_activity_number === 'number' && Number.isFinite(metadata.from_driver_activity_number)
        ? metadata.from_driver_activity_number
        : Number.MAX_SAFE_INTEGER;
    const toActivity =
      typeof metadata?.to_driver_activity_number === 'number' && Number.isFinite(metadata.to_driver_activity_number)
        ? metadata.to_driver_activity_number
        : Number.MAX_SAFE_INTEGER;
    const first = latLng[0];
    const last = latLng[latLng.length - 1];
    const dedupeKey = `${shiftId}|${segmentIndex}|${fromActivity}|${toActivity}|${latLng.length}|${first[0].toFixed(6)},${first[1].toFixed(6)}|${last[0].toFixed(6)},${last[1].toFixed(6)}`;
    if (seen.has(dedupeKey)) return;
    seen.add(dedupeKey);
    rows.push({ shiftId, segmentIndex, fromActivity, toActivity, routeId, geometry: latLng });
  });

  const byShift: ShiftGeometrySeed = {};
  rows
    .sort((a, b) => {
      if (a.shiftId !== b.shiftId) return a.shiftId.localeCompare(b.shiftId);
      if (a.segmentIndex !== b.segmentIndex) return a.segmentIndex - b.segmentIndex;
      if (a.fromActivity !== b.fromActivity) return a.fromActivity - b.fromActivity;
      if (a.toActivity !== b.toActivity) return a.toActivity - b.toActivity;
      return a.routeId.localeCompare(b.routeId);
    })
    .forEach((row) => {
      if (!byShift[row.shiftId]) byShift[row.shiftId] = [];
      byShift[row.shiftId].push(row.geometry);
    });

  return byShift;
}

function buildShiftRouteMetadataSeedFromRoadRoutes(routesById: RoadRouteByRideId): ShiftRouteMetadataSeed {
  type SeedRow = {
    shiftId: string;
    segmentIndex: number;
    fromActivity: number;
    toActivity: number;
    routeId: string;
    metadata: SegmentRouteMetadata;
  };
  const rows: SeedRow[] = [];
  const seen = new Set<string>();

  Object.entries(routesById).forEach(([routeId, route]) => {
    const metadata = route?.metadata;
    const shiftId = metadata?.vehicle_shift_id?.trim();
    if (!metadata || !shiftId) return;
    const segmentIndex =
      typeof metadata.segment_index === 'number' && Number.isFinite(metadata.segment_index)
        ? metadata.segment_index
        : Number.MAX_SAFE_INTEGER;
    const fromActivity =
      typeof metadata.from_driver_activity_number === 'number' && Number.isFinite(metadata.from_driver_activity_number)
        ? metadata.from_driver_activity_number
        : Number.MAX_SAFE_INTEGER;
    const toActivity =
      typeof metadata.to_driver_activity_number === 'number' && Number.isFinite(metadata.to_driver_activity_number)
        ? metadata.to_driver_activity_number
        : Number.MAX_SAFE_INTEGER;
    const dedupeKey = `${shiftId}|${segmentIndex}|${fromActivity}|${toActivity}|${routeId}`;
    if (seen.has(dedupeKey)) return;
    seen.add(dedupeKey);
    rows.push({ shiftId, segmentIndex, fromActivity, toActivity, routeId, metadata });
  });

  const byShift: ShiftRouteMetadataSeed = {};
  rows
    .sort((a, b) => {
      if (a.shiftId !== b.shiftId) return a.shiftId.localeCompare(b.shiftId);
      if (a.segmentIndex !== b.segmentIndex) return a.segmentIndex - b.segmentIndex;
      if (a.fromActivity !== b.fromActivity) return a.fromActivity - b.fromActivity;
      if (a.toActivity !== b.toActivity) return a.toActivity - b.toActivity;
      return a.routeId.localeCompare(b.routeId);
    })
    .forEach((row) => {
      if (!byShift[row.shiftId]) byShift[row.shiftId] = [];
      byShift[row.shiftId].push(row.metadata);
    });

  return byShift;
}

function getRideRouteLookupKeys(point: Record<string, unknown>): string[] {
  const keys: string[] = [];
  RIDE_ROUTE_KEY_FIELDS.forEach((field) => {
    const value = point[field];
    if (typeof value === 'string' && value.trim() !== '') {
      keys.push(value);
      return;
    }
    if (typeof value === 'number' && Number.isFinite(value)) {
      keys.push(String(value));
    }
  });
  return Array.from(new Set(keys));
}

function interpolateHexColor(startHex: string, endHex: string, t: number): string {
  const clamp = Math.max(0, Math.min(1, t));
  const s = startHex.replace('#', '');
  const e = endHex.replace('#', '');
  const sr = parseInt(s.slice(0, 2), 16);
  const sg = parseInt(s.slice(2, 4), 16);
  const sb = parseInt(s.slice(4, 6), 16);
  const er = parseInt(e.slice(0, 2), 16);
  const eg = parseInt(e.slice(2, 4), 16);
  const eb = parseInt(e.slice(4, 6), 16);
  const r = Math.round(sr + (er - sr) * clamp);
  const g = Math.round(sg + (eg - sg) * clamp);
  const b = Math.round(sb + (eb - sb) * clamp);
  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b
    .toString(16)
    .padStart(2, '0')}`;
}

function densifyLine(from: [number, number], to: [number, number], steps = 16): [number, number][] {
  const points: [number, number][] = [];
  for (let i = 0; i <= steps; i++) {
    const t = i / steps;
    points.push([from[0] + (to[0] - from[0]) * t, from[1] + (to[1] - from[1]) * t]);
  }
  return points;
}

/** Display/em-dash for missing values (UTF-8 em dash, not legacy mojibake). */
const EM_DASH = '\u2014';

type MapTripInfoPanelData = {
  tripId: string;
  requestTime: string;
  /** Pickup stop time from activities (ISO or placeholder), not geographic coords. */
  pickupStopTime: string;
  /** Dropoff stop time from activities (ISO or placeholder). */
  dropoffStopTime: string;
  totalTravelTime: string;
  /** Explains how totalTravelTime was calculated (for Tooltip). */
  travelTimeTooltip: string;
  passengers: string;
  /** Primary passenger / user id when available. */
  passengerUserId: string;
  pickupVehicle: string;
  /** Stable internal marker for unallocated rides (do not infer from translated labels). */
  isUnallocated: boolean;
  /** Pickup / dropoff endpoints for Google Maps (from ride point or unmatched ride). */
  mapsFromLat?: number;
  mapsFromLng?: number;
  mapsToLat?: number;
  mapsToLng?: number;
  mapsFromAddress?: string;
  mapsToAddress?: string;
  /** Heavy ids — same set as unmatched rides info tooltip; copy per row. */
  identifierRows: { label: string; value: string }[];
};

function formatPassengerIdForTripPanel(id: string): { display: string; tooltip: string } {
  if (id === 'â€”') return { display: 'â€”', tooltip: '' };
  const t = id.trim();
  if (t.length <= 22) return { display: t, tooltip: t };
  return { display: `${t.slice(0, 10)}â€¦`, tooltip: t };
}

function formatCoordPair(lat?: unknown, lng?: unknown): string {
  const a = coerceRideCoordinate(lat);
  const b = coerceRideCoordinate(lng);
  if (a == null || b == null) return EM_DASH;
  return `${a.toFixed(5)}, ${b.toFixed(5)}`;
}

function latLonClipboardText(lat?: unknown, lng?: unknown): string | null {
  const a = coerceRideCoordinate(lat);
  const b = coerceRideCoordinate(lng);
  if (a == null && b == null) return null;
  if (a != null && b != null) return `${a.toFixed(6)}, ${b.toFixed(6)}`;
  return a != null ? String(a) : b != null ? String(b) : null;
}

/** Activity stop coordinates; coerces numeric strings from API payloads. */
function getActivityLatLngCoerced(activity: SimulationActivity): { lat: number; lng: number } | null {
  const r = activity as Record<string, unknown>;
  const lat = coerceRideCoordinate(activity.activity_latitude ?? activity.latitude ?? r.lat);
  const lng = coerceRideCoordinate(activity.activity_longitude ?? activity.longitude ?? r.lng ?? r.lon);
  if (lat != null && lng != null) return { lat, lng };
  return null;
}

/**
 * When ride_points row is missing or incomplete, infer O/D from matching activities
 * (earliest pickup vs latest dropoff by parsed start time).
 */
function inferMapsEndpointsFromMatchingActivities(
  matchingActivities: SimulationActivity[],
  mapsFromLat: number | undefined,
  mapsFromLng: number | undefined,
  mapsToLat: number | undefined,
  mapsToLng: number | undefined
): { mapsFromLat?: number; mapsFromLng?: number; mapsToLat?: number; mapsToLng?: number } {
  let fl = mapsFromLat;
  let fn = mapsFromLng;
  let tl = mapsToLat;
  let tn = mapsToLng;
  if (fl != null && fn != null && tl != null && tn != null) {
    return { mapsFromLat: fl, mapsFromLng: fn, mapsToLat: tl, mapsToLng: tn };
  }

  let earliestPickup: { ms: number; lat: number; lng: number } | null = null;
  let latestDrop: { ms: number; lat: number; lng: number } | null = null;

  for (const a of matchingActivities) {
    const c = getActivityLatLngCoerced(a);
    if (!c) continue;
    const kind = activityStopKindForUi(a);
    const ms =
      parseValidIsoMs(a.activity_start_time) ??
      parseValidIsoMs(a.activity_start) ??
      parseValidIsoMs(a.min_activity_start_time) ??
      parseValidIsoMs(a.start_time) ??
      parseValidIsoMs(a.time) ??
      null;
    const t = ms ?? 0;
    if (kind === 'pickup') {
      if (earliestPickup == null || t < earliestPickup.ms) earliestPickup = { ms: t, ...c };
    }
    if (kind === 'dropoff') {
      if (latestDrop == null || t > latestDrop.ms) latestDrop = { ms: t, ...c };
    }
  }

  if ((fl == null || fn == null) && earliestPickup) {
    fl = earliestPickup.lat;
    fn = earliestPickup.lng;
  }
  if ((tl == null || tn == null) && latestDrop) {
    tl = latestDrop.lat;
    tn = latestDrop.lng;
  }
  return { mapsFromLat: fl, mapsFromLng: fn, mapsToLat: tl, mapsToLng: tn };
}

function buildTripPanelIdentifierRowsFromRidePoint(
  matchedRidePoint: Record<string, unknown> | null | undefined,
  t: (key: string) => string
): { label: string; value: string }[] {
  if (!matchedRidePoint) return [];
  const r = matchedRidePoint;
  const specs: { label: string; keys: readonly string[] }[] = [
    { label: t('simResultsMenu.tripPanel.identifiers.rideId'), keys: ['ride_id', 'rideId'] },
    { label: t('simResultsMenu.tripPanel.identifiers.requestId'), keys: ['request_id', 'requestId'] },
    { label: t('simResultsMenu.tripPanel.identifiers.externalRef'), keys: ['external_reference_id', 'externalReferenceId'] },
    { label: t('simResultsMenu.tripPanel.identifiers.driverRideId'), keys: ['driver_ride_id', 'driverRideId'] },
    { label: t('simResultsMenu.tripPanel.identifiers.riderRideId'), keys: ['rider_ride_id', 'riderRideId'] },
    { label: t('simResultsMenu.tripPanel.identifiers.userRef'), keys: ['external_user_reference_id', 'externalUserReferenceId'] },
    { label: t('simResultsMenu.tripPanel.identifiers.userId'), keys: ['user_id', 'userId'] },
    { label: t('simResultsMenu.tripPanel.identifiers.batchId'), keys: ['batch_id', 'batchId'] },
    { label: t('simResultsMenu.tripPanel.identifiers.resultsId'), keys: ['results_id', 'resultsId'] },
  ];
  const rows: { label: string; value: string }[] = [];
  for (const { label, keys } of specs) {
    const v = firstDefinedString(keys.map((k) => r[k]));
    if (v) rows.push({ label, value: v });
  }
  return rows;
}

function buildTripPanelIdentifierRowsFromUnmatchedRide(ride: UnmatchedRide): { label: string; value: string }[] {
  return UNMATCHED_RIDE_IDENTIFIER_COLUMNS.map((col) => ({ label: col.label, value: col.get(ride) }))
    .filter((row): row is { label: string; value: string } => row.value != null && String(row.value).trim() !== '');
}

/** Directions / info / close on the map trip bar — shared glass pill look */
const tripPanelToolbarIconButtonSx = {
  color: 'rgba(255,255,255,0.88)',
  backgroundColor: 'rgba(255,255,255,0.08)',
  border: '1px solid rgba(255,255,255,0.15)',
  '&:hover': { backgroundColor: 'rgba(255,255,255,0.16)' },
  '&:focus-visible': {
    outline: '2px solid rgba(157, 203, 255, 0.95)',
    outlineOffset: 2,
  },
} as const;

const tripPanelToolbarIconButtonDisabledSx = {
  '&.Mui-disabled': {
    color: 'rgba(255,255,255,0.35)',
    borderColor: 'rgba(255,255,255,0.1)',
  },
} as const;

const TripPanelIdentifiersIcon: React.FC<{ rows?: TripPanelDetailTooltipRow[] | null }> = ({ rows }) => {
  const { t } = useTranslation('mapCore');
  const safeRows = Array.isArray(rows) ? rows : [];
  if (safeRows.length === 0) return null;
  const fontFamily = '"Roboto", system-ui, -apple-system, "Segoe UI", sans-serif';
  const fs = '0.8125rem';
  const labelFs = '0.75rem';
  const MONO_FONT = 'ui-monospace, "Cascadia Code", "Segoe UI Mono", Menlo, Consolas, monospace';

  return (
    <Tooltip
      title={
        <Box sx={{ fontFamily, fontSize: fs, maxWidth: 380, py: 0.25 }}>
          {safeRows.map(({ label, value, copyText }, rowIndex) => {
            const stripe =
              rowIndex % 2 === 0
                ? UNMATCHED_RIDE_TOOLTIP_THEME.rowStripeEven
                : UNMATCHED_RIDE_TOOLTIP_THEME.rowStripeOdd;
            return (
              <Box
                key={`${label}-${rowIndex}`}
                sx={{
                  display: 'flex',
                  gap: 0.75,
                  alignItems: 'flex-start',
                  py: 0.35,
                  px: 0.75,
                  mx: -0.25,
                  borderRadius: 0.5,
                  backgroundColor: stripe,
                }}
              >
                <Typography
                  component="span"
                  sx={{
                    color: UNMATCHED_RIDE_TOOLTIP_THEME.label,
                    fontWeight: 600,
                    fontSize: labelFs,
                    minWidth: 92,
                    flexShrink: 0,
                    letterSpacing: '0.01em',
                  }}
                >
                  {label}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0, flex: 1, minWidth: 0 }}>
                  <Typography
                    component="span"
                    sx={{
                      color: UNMATCHED_RIDE_TOOLTIP_THEME.value,
                      fontSize: fs,
                      fontWeight: 500,
                      wordBreak: 'break-word',
                      fontFamily: MONO_FONT,
                      flex: 1,
                      minWidth: 0,
                    }}
                  >
                    {value}
                  </Typography>
                  <QuickCopyButton
                    textToCopy={copyText ?? value}
                    ariaLabel={t('simResultsMenu.tripPanel.copyAria', { label })}
                    tone="onLight"
                  />
                </Box>
              </Box>
            );
          })}
        </Box>
      }
      placement="left"
      arrow
      enterDelay={120}
      leaveDelay={280}
      disableInteractive={false}
      slotProps={{
        popper: { sx: unmatchedRideTooltipPopperSx },
      }}
    >
      <IconButton
        type="button"
        size="small"
        aria-label={t('simResultsMenu.tripPanel.detailsAria')}
        onClick={(e) => {
          e.stopPropagation();
        }}
        sx={{ ...tripPanelToolbarIconButtonSx }}
      >
        <Icon size={16} name="circle-info" />
      </IconButton>
    </Tooltip>
  );
};

/** HH:mm UTC for Google Maps tooltip copy (matches Operations timeline convention). */
function formatTripPanelRequestTimeUtcHm(requestTime: string): string {
  const t = requestTime.trim();
  if (t === '' || t === EM_DASH || t === 'â€”') return EM_DASH;
  const ms = parseRequestTimeToMs(t) ?? (Number.isNaN(Date.parse(t)) ? null : Date.parse(t));
  if (ms == null) return t.length > 36 ? `${t.slice(0, 33)}â€¦` : t;
  const d = new Date(ms);
  return `${d.getUTCHours().toString().padStart(2, '0')}:${d.getUTCMinutes().toString().padStart(2, '0')}`;
}

const TRIP_PANEL_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'] as const;

/** Shorter UTC string for trip bar (avoids full ISO + ms + Z). Full value stays in tooltip. */
function formatTripPanelTimeCompact(raw: string): string {
  const t = raw.trim();
  if (t === '' || t === EM_DASH || t === '\u2014' || t === 'â€”') return EM_DASH;
  const ms = parseRequestTimeToMs(t) ?? (Number.isNaN(Date.parse(t)) ? null : Date.parse(t));
  if (ms == null) return t.length > 22 ? `${t.slice(0, 20)}\u2026` : t;
  const d = new Date(ms);
  const mo = TRIP_PANEL_MONTHS[d.getUTCMonth()];
  const day = d.getUTCDate();
  const hh = d.getUTCHours().toString().padStart(2, '0');
  const mm = d.getUTCMinutes().toString().padStart(2, '0');
  const ss = d.getUTCSeconds().toString().padStart(2, '0');
  return `${mo} ${day}, ${hh}:${mm}:${ss} UTC`;
}

/** UTC clock time only (no date), for compact trip bar cells. */
function formatTripPanelTimeOnlyUtc(raw: string): string {
  const t = raw.trim();
  if (t === '' || t === EM_DASH || t === '\u2014' || t === 'â€”') return EM_DASH;
  const ms = parseRequestTimeToMs(t) ?? (Number.isNaN(Date.parse(t)) ? null : Date.parse(t));
  if (ms == null) return t.length > 12 ? `${t.slice(0, 10)}\u2026` : t;
  const d = new Date(ms);
  return `${d.getUTCHours().toString().padStart(2, '0')}:${d.getUTCMinutes().toString().padStart(2, '0')}:${d.getUTCSeconds().toString().padStart(2, '0')}`;
}

type TripPanelDetailTooltipRow = { label: string; value: string; copyText?: string };

/**
 * Details tooltip: trip id, user, request (time-only label) + existing identifier rows, deduped.
 * `copyText` when set is used for quick-copy (e.g. full ISO for request time).
 */
function mergeTripPanelDetailRows(
  d: MapTripInfoPanelData,
  t: (key: string) => string
): TripPanelDetailTooltipRow[] {
  const out: TripPanelDetailTooltipRow[] = [];
  const tripIdVal = String(d.tripId ?? '').trim();
  if (tripIdVal && tripIdVal !== EM_DASH && tripIdVal !== 'â€”') {
    out.push({ label: t('simResultsMenu.tripPanel.identifiers.tripId'), value: tripIdVal, copyText: tripIdVal });
  }
  const userVal = String(d.passengerUserId ?? '').trim();
  const noUser =
    !userVal || userVal === EM_DASH || userVal === 'â€”' || userVal === '\u2014';
  if (!noUser) {
    out.push({ label: t('simResultsMenu.tripPanel.identifiers.user'), value: userVal, copyText: userVal });
  }
  const req = String(d.requestTime ?? '').trim();
  if (req && req !== EM_DASH && req !== 'â€”') {
    const timeOnly = formatTripPanelTimeOnlyUtc(req);
    out.push({
      label: t('simResultsMenu.tripPanel.identifiers.requestTime'),
      value: timeOnly === EM_DASH ? req : timeOnly,
      copyText: req,
    });
  }
  const rideIdLabel = t('simResultsMenu.tripPanel.identifiers.rideId');
  const userIdLabel = t('simResultsMenu.tripPanel.identifiers.userId');
  const userRefLabel = t('simResultsMenu.tripPanel.identifiers.userRef');
  const rest = (d.identifierRows ?? []).filter((r) => {
    if (r.label === rideIdLabel && r.value === tripIdVal) return false;
    if ((r.label === userIdLabel || r.label === userRefLabel) && r.value === userVal) return false;
    return true;
  });
  for (const r of rest) {
    out.push({ label: r.label, value: r.value, copyText: r.value });
  }
  return out;
}

const tripPanelStatLabelSx = {
  fontSize: '0.62rem',
  fontWeight: 600,
  letterSpacing: '0.07em',
  textTransform: 'uppercase' as const,
  color: 'rgba(247, 249, 255, 0.52)',
  lineHeight: 1.2,
  display: 'block',
  mb: 0.35,
};

/** One label-above-value block for the map trip details bar. */
const TripPanelStat: React.FC<{
  label: string;
  icon?: React.ReactNode;
  children: React.ReactNode;
  /** When set, label and value row are centered (trip panel route + stats columns). */
  align?: 'start' | 'center';
}> = ({ label, icon, children, align = 'start' }) => {
  const centered = align === 'center';
  return (
    <Box
      sx={{
        minWidth: 0,
        width: centered ? '100%' : undefined,
        display: 'flex',
        flexDirection: 'column',
        alignItems: centered ? 'center' : 'stretch',
      }}
    >
      <Typography
        component="span"
        sx={{
          ...tripPanelStatLabelSx,
          ...(centered ? { textAlign: 'center', alignSelf: 'center' } : {}),
        }}
      >
        {label}
      </Typography>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.45,
          minWidth: 0,
          width: centered ? '100%' : undefined,
          justifyContent: centered ? 'center' : 'flex-start',
        }}
      >
        {icon ? <Box sx={{ display: 'flex', flexShrink: 0, opacity: 0.92 }}>{icon}</Box> : null}
        <Box
          sx={{
            minWidth: 0,
            flex: centered ? '0 1 auto' : 1,
            display: centered ? 'flex' : 'block',
            justifyContent: centered ? 'center' : undefined,
          }}
        >
          {children}
        </Box>
      </Box>
    </Box>
  );
};

function parseValidIsoMs(value: unknown): number | null {
  if (typeof value !== 'string' || value.trim() === '') return null;
  const parsed = Date.parse(value);
  if (Number.isNaN(parsed)) return null;
  // Ignore epoch/sentinel placeholders seen in activities API.
  if (parsed < 86_400_000) return null;
  return parsed;
}

function firstDefinedString(values: unknown[]): string | null {
  for (const v of values) {
    if (typeof v === 'string' && v.trim() !== '') return v;
  }
  return null;
}

function resolveResultsId(runId: string, summary?: Partial<SimulationRunSummary> | null): string {
  const resultsId = summary?.results_id;
  if (typeof resultsId === 'string' && resultsId.trim() !== '') {
    return resultsId;
  }
  return runId;
}

function resolveCanonicalResultsId(
  runId: string,
  summary: Partial<SimulationRunSummary> | null | undefined,
  allResults: SimulationRunSummary[],
  history: SimulationRunSummary[]
): string | null {
  const candidateIds = [
    summary?.results_id,
    allResults.find((sim) => sim.run_id === runId)?.results_id,
    history.find((sim) => sim.run_id === runId)?.results_id,
  ];

  for (const candidate of candidateIds) {
    if (typeof candidate === 'string' && candidate.trim() !== '') {
      return candidate;
    }
  }

  return null;
}

function normalizeTripId(value: unknown): string | null {
  if (value == null) return null;
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  const s = String(value).trim();
  return s.length > 0 ? s : null;
}

function unmatchedRideHasMapCoords(ride: UnmatchedRide): boolean {
  return (
    typeof ride.from_latitude === 'number' &&
    typeof ride.from_longitude === 'number' &&
    typeof ride.to_latitude === 'number' &&
    typeof ride.to_longitude === 'number'
  );
}

function tripIdFromActivity(activity: SimulationActivity): string | null {
  const a = activity as Record<string, unknown>;
  return normalizeTripId(
    activity.ride_id ??
      activity.request_id ??
      a.requestId ??
      a.rider_ride_id ??
      a.driver_ride_id ??
      a.external_reference_id
  );
}

/** Delegates to shared helper so keys include nested `trips[].ride` ids (ride_id, external_reference_id, â€¦). */
function collectActivityIdCandidates(activity: SimulationActivity): string[] {
  return collectActivityIdCandidatesForTripFocus(activity);
}

/** Delegates to the shared pure helper so ride points index under the same ride-level trip
 * identity as activities (user id excluded — see `collectRidePointIdCandidatesForTripFocus`). */
function collectRidePointIdCandidates(point: Record<string, unknown>): string[] {
  return collectRidePointIdCandidatesForTripFocus(point);
}

type RidePointCoordEntry = {
  /** Null when missing or non-numeric in payload (see {@link coerceRideCoordinate}). */
  fromLat: number | null;
  fromLng: number | null;
  toLat: number | null;
  toLng: number | null;
  keys: string[];
};

/**
 * Fast version of deriveTripSeedFromActivity using a pre-built coord index instead of
 * scanning the raw ridePoints array on every call.
 */
function deriveTripSeedFast(
  activity: SimulationActivity,
  coordIndex: RidePointCoordEntry[]
): string[] {
  const direct = collectActivityIdCandidates(activity);
  if (direct.length > 0) return direct;
  if (coordIndex.length === 0) return direct;
  const coords = getActivityCoordinates(activity);
  if (!coords) return direct;
  const stopKind = activityStopKindForUi(activity);
  const threshold = 550;
  let bestKeys: string[] = [];
  let bestDist = Infinity;
  for (const entry of coordIndex) {
    if (entry.keys.length === 0) continue;
    if (stopKind !== 'dropoff' && entry.fromLat != null && entry.fromLng != null) {
      const d = haversineDistanceMeters(coords.lat, coords.lng, entry.fromLat, entry.fromLng);
      if (d < bestDist) { bestDist = d; bestKeys = entry.keys; }
    }
    if (stopKind !== 'pickup' && entry.toLat != null && entry.toLng != null) {
      const d = haversineDistanceMeters(coords.lat, coords.lng, entry.toLat, entry.toLng);
      if (d < bestDist) { bestDist = d; bestKeys = entry.keys; }
    }
  }
  return bestDist <= threshold ? bestKeys : direct;
}

function expandTripMatchKeysFromIndex(
  seedKeys: string[],
  index: Map<string, Set<string>>
): string[] {
  if (seedKeys.length === 0) return [];
  const merged = new Set<string>(seedKeys);
  for (const k of seedKeys) {
    const aliases = index.get(k);
    if (aliases) aliases.forEach((v) => merged.add(v));
  }
  return Array.from(merged);
}

function ridePointMatchesTripKeys(point: Record<string, unknown>, matchKeys: Set<string> | string[] | null): boolean {
  if (!matchKeys) return false;
  const keySet = matchKeys instanceof Set ? matchKeys : new Set(matchKeys);
  if (keySet.size === 0) return false;
  return collectRidePointIdCandidates(point).some((k) => keySet.has(k));
}

// Trip-route matching + gradient-merge helpers now live in
// `src/utils/simulation/tripRouteMatching.ts` (pure + unit-tested).

/** Halos at focused trip endpoints (pickup green, dropoff orange). */
function TripStopsHighlightCircles({
  matchKeys,
  activities,
  fallbackRidePoint,
}: {
  matchKeys: string[];
  activities: SimulationActivity[] | undefined;
  fallbackRidePoint: Record<string, unknown> | null;
}) {
  if (matchKeys.length === 0) return null;
  const keyTag = matchKeys.slice(0, 3).join('|');
  const positions: Array<{ center: [number, number]; kind: 'pickup' | 'dropoff' }> = [];
  if (activities?.length) {
    activities.forEach((activity) => {
      if (!activityMatchesTripKeys(activity, matchKeys)) return;
      const coords = getActivityCoordinates(activity);
      if (!coords) return;
      const kind = activityStopKindForUi(activity);
      if (kind === 'pickup' || kind === 'dropoff') {
        positions.push({ center: [coords.lat, coords.lng], kind });
      }
    });
  }
  if (positions.length === 0 && fallbackRidePoint) {
    const fromLat = fallbackRidePoint.from_latitude;
    const fromLng = fallbackRidePoint.from_longitude;
    const toLat = fallbackRidePoint.to_latitude;
    const toLng = fallbackRidePoint.to_longitude;
    if (typeof fromLat === 'number' && typeof fromLng === 'number') {
      positions.push({ center: [fromLat, fromLng], kind: 'pickup' });
    }
    if (typeof toLat === 'number' && typeof toLng === 'number') {
      positions.push({ center: [toLat, toLng], kind: 'dropoff' });
    }
  }
  if (positions.length === 0) return null;
  return (
    <Pane name="tripHighlightStops" style={{ zIndex: 560 }}>
      {positions.map(({ center, kind }, idx) => (
        <Circle
          key={`trip-stop-${kind}-${keyTag}-${idx}-${center[0]}-${center[1]}`}
          center={center}
          radius={88}
          pathOptions={{
            color: kind === 'pickup' ? '#5FC98F' : '#EE7334',
            fillColor: kind === 'pickup' ? '#5FC98F' : '#EE7334',
            fillOpacity: 0.22,
            weight: 3,
            opacity: 1,
          }}
        />
      ))}
    </Pane>
  );
}

/** Halos at focused unallocated trip endpoints (neutral grey, distinct from allocated green/orange). */
function UnmatchedTripStopsHighlightCircles({ ride }: { ride: UnmatchedRide | null }) {
  if (!ride) return null;
  const fromLat = ride.from_latitude;
  const fromLng = ride.from_longitude;
  const toLat = ride.to_latitude;
  const toLng = ride.to_longitude;
  const positions: [number, number][] = [];
  if (typeof fromLat === 'number' && typeof fromLng === 'number') {
    positions.push([fromLat, fromLng]);
  }
  if (typeof toLat === 'number' && typeof toLng === 'number') {
    positions.push([toLat, toLng]);
  }
  if (positions.length === 0) return null;
  const keyTag = unmatchedRideStableKey(ride);
  return (
    <Pane name="unmatchedHighlightStops" style={{ zIndex: 562 }}>
      {positions.map((center, idx) => (
        <Circle
          key={`unmatched-stop-${keyTag}-${idx}-${center[0]}-${center[1]}`}
          center={center}
          radius={88}
          pathOptions={{
            color: UNMATCHED_STOP_GREY,
            fillColor: UNMATCHED_STOP_GREY,
            fillOpacity: 0.2,
            weight: 3,
            opacity: 1,
          }}
        />
      ))}
    </Pane>
  );
}

const SimulationResultsMenu: React.FC<SimulationResultsMenuProps> = ({ open, onClose, authToken, variant = 'drawer' }) => {
  const { t } = useTranslation('mapCore');
  const metrics = React.useMemo(() => buildMetrics(t), [t]);
  const SIDEBAR_METRICS = React.useMemo(
    () => metrics.map(({ label, field, unit }) => ({ label, field, unit })),
    [metrics]
  );
  const navigate = useNavigate();
  const isPage = variant === 'page';
  const appCtx = React.useContext(AppContext);
  const currentUserEmail = appCtx?.userData?.userDetails?.email;
  const resultsCtx = useSimulationResultsContext();
  const googleBasemap = useGoogleBasemap();
  const { importSimulationResultsNetwork } = useFixedRoutes();
  const [selectedRunIdLocal, setSelectedRunIdLocal] = useState<string | null>(null);
  const [selectedSim, setSelectedSim] = useState<SimulationRunSummary | null>(null);
  const [shareResultsId, setShareResultsId] = useState<string | null>(null);
  const [shareResultsTitle, setShareResultsTitle] = useState<string>('');
  const [shareRole, setShareRole] = useState<'Owner' | 'Editor' | 'Viewer' | undefined>(undefined);
  const [ridePoints, setRidePoints] = useState<any[] | null>(null);

  const vehicleOpsResult = useVehicleOperations(selectedSim?.activities ?? [], {
    co2RateKgPerKm: DEFAULT_CO2_RATE_KG_PER_KM,
  });
  const vehicleKeyToLabel = React.useMemo(() => {
    const m: Record<string, string> = {};
    vehicleOpsResult.vehicles.forEach((v) => {
      m[v.vehicleId] = v.displayName;
    });
    return m;
  }, [vehicleOpsResult.vehicles]);
  const [ridesError, setRidesError] = useState<string | null>(null);
  const [manualRunId, setManualRunId] = useState('');
  const [manualLoading, setManualLoading] = useState(false);
  const [manualError, setManualError] = useState<string | null>(null);
  const [timelineTime, setTimelineTime] = useState<Date | null>(null);
  const [timelineWindowMinutes, setTimelineWindowMinutes] = useState<number>(24 * 60); // 24 hours to show full day by default
  const [simulationResults, setSimulationResults] = useState<SimulationRunSummary[]>([]);
  const [loadingResults, setLoadingResults] = useState(false);
  const [resultsError, setResultsError] = useState<string | null>(null);
  const [loadingDetailedData, setLoadingDetailedData] = useState(false);
  const [detailedDataError, setDetailedDataError] = useState<string | null>(null);
  const [highlightedActivity, setHighlightedActivity] = useState<{
    lat: number;
    lng: number;
    activityId?: string;
  } | null>(null);
  /** When set, map dims other trips and emphasizes this trip's stops and path geometry. */
  const [highlightedTripRideId, setHighlightedTripRideId] = useState<string | null>(null);
  /** All known id aliases for the focused trip (ride_id, request_id, â€¦) after expansion. */
  const [highlightedTripMatchKeys, setHighlightedTripMatchKeys] = useState<string[]>([]);
  const [mapTripInfoPanelData, setMapTripInfoPanelData] = useState<MapTripInfoPanelData | null>(null);

  /** Pre-built trip ID alias index: maps every known ID to the full set of aliases for that trip. */
  const tripIdIndex = React.useMemo((): Map<string, Set<string>> => {
    const index = new Map<string, Set<string>>();
    const register = (keys: string[]) => {
      if (keys.length === 0) return;
      const merged = new Set<string>(keys);
      for (const k of keys) {
        const existing = index.get(k);
        if (existing) existing.forEach((v) => merged.add(v));
      }
      merged.forEach((k) => index.set(k, merged));
    };
    selectedSim?.activities?.forEach((act) => register(collectActivityIdCandidates(act)));
    ridePoints?.forEach((p) => register(collectRidePointIdCandidates(p as Record<string, unknown>)));
    return index;
  }, [selectedSim?.activities, ridePoints]);

  /** Pre-flattened coord index for O(N) haversine fallback in deriveTripSeedFast. */
  const ridePointCoordIndex = React.useMemo(
    (): RidePointCoordEntry[] =>
      (ridePoints ?? []).map((p) => ({
        fromLat: coerceRideCoordinate((p as Record<string, unknown>).from_latitude),
        fromLng: coerceRideCoordinate((p as Record<string, unknown>).from_longitude),
        toLat: coerceRideCoordinate((p as Record<string, unknown>).to_latitude),
        toLng: coerceRideCoordinate((p as Record<string, unknown>).to_longitude),
        keys: collectRidePointIdCandidates(p as Record<string, unknown>),
      })),
    [ridePoints]
  );
  /** Full-page map: focused unallocated ride (dims allocated geometry; dark grey Oâ€“D markers). */
  const [highlightedUnmatchedRide, setHighlightedUnmatchedRide] = useState<UnmatchedRide | null>(null);
  /** Full-page map + Operations: show unallocated Oâ€“D, markers, and timeline row. */
  const [unallocatedVisible, setUnallocatedVisible] = useState(true);
  /** Full-page map: show road polylines from /paths when available, or straight originâ€“destination segments. */
  const [selectedVehicleForDetailPanel, setSelectedVehicleForDetailPanel] = useState<VehicleOperation | null>(null);
  const [roadRoutesByRideId, setRoadRoutesByRideId] = useState<RoadRouteByRideId>({});
  const [vehicleRoutes, setVehicleRoutes] = useState<AddressPairRoute[]>([]);
  const [isGeneratingRoadRoutes, setIsGeneratingRoadRoutes] = useState(false);
  const [routeGenerationSummary, setRouteGenerationSummary] = useState<{
    total: number;
    succeeded: number;
    failed: number;
  } | null>(null);
  const [routeFailureWarning, setRouteFailureWarning] = useState<string | null>(null);
  const [importFixedRoutesConfirmOpen, setImportFixedRoutesConfirmOpen] = useState(false);
  const [isImportingFixedRoutes, setIsImportingFixedRoutes] = useState(false);
  const [importFixedRoutesMessage, setImportFixedRoutesMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [isNavigatingToMap, setIsNavigatingToMap] = useState(false);
  const [isAddingOnDemandToMap, setIsAddingOnDemandToMap] = useState(false);
  const [addToMapProgressText, setAddToMapProgressText] = useState<string | null>(null);
  const [vehicleVisibilityMap, setVehicleVisibilityMap] = useState<Record<string, boolean>>({});
  const [deckTotalHeight, setDeckTotalHeight] = useState(ANALYTICS_DECK_CHROME_HEIGHT + ANALYTICS_DECK_CONTENT_HEIGHT_DEFAULT);
  const context = React.useContext(AppContext);

  const handleBackToMap = React.useCallback(() => {
    if (isNavigatingToMap) return;
    setIsNavigatingToMap(true);
    // Let the loading overlay paint before mounting the heavy map route.
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => navigate('/'));
    });
  }, [isNavigatingToMap, navigate]);

  const yieldToPaint = React.useCallback(
    () => new Promise<void>((resolve) => window.requestAnimationFrame(() => resolve())),
    []
  );

  /** Stable Set version of highlightedTripMatchKeys â€” avoids per-marker new Set() allocation in render. */
  const highlightedTripMatchKeySet = React.useMemo(
    () => new Set(highlightedTripMatchKeys),
    [highlightedTripMatchKeys]
  );

  /** Map<id, ridePoint> built once per ridePoints load â€” O(matchKeys) lookup vs O(ridePoints) scan. */
  const ridePointByIdMap = React.useMemo((): Map<string, Record<string, unknown>> => {
    const map = new Map<string, Record<string, unknown>>();
    (ridePoints ?? []).forEach((p, idx) => {
      const entry = ridePointCoordIndex[idx];
      if (entry) entry.keys.forEach((k) => map.set(k, p as Record<string, unknown>));
    });
    return map;
  }, [ridePoints, ridePointCoordIndex]);

  const highlightedTripRidePoint = React.useMemo((): Record<string, unknown> | null => {
    if (highlightedTripMatchKeySet.size === 0) return null;
    let found: Record<string, unknown> | null = null;
    highlightedTripMatchKeySet.forEach((k) => {
      if (!found) found = ridePointByIdMap.get(k) ?? null;
    });
    return found;
  }, [highlightedTripMatchKeySet, ridePointByIdMap]);

  /** Activities that belong to the focused trip â€” O(N) scan once, O(1) per-marker lookup. */
  const matchingActivitySet = React.useMemo((): Set<SimulationActivity> => {
    if (highlightedTripMatchKeySet.size === 0) return new Set();
    const result = new Set<SimulationActivity>();
    for (const act of selectedSim?.activities ?? []) {
      if (activityMatchesTripKeys(act, highlightedTripMatchKeySet)) result.add(act);
    }
    return result;
  }, [selectedSim?.activities, highlightedTripMatchKeySet]);

  /** Ride points that belong to the focused trip â€” O(N) scan once, O(1) per-marker lookup. */
  const matchingRidePointSet = React.useMemo((): Set<unknown> => {
    if (highlightedTripMatchKeySet.size === 0) return new Set();
    const result = new Set<unknown>();
    for (const p of ridePoints ?? []) {
      if (ridePointMatchesTripKeys(p as Record<string, unknown>, highlightedTripMatchKeySet)) result.add(p);
    }
    return result;
  }, [ridePoints, highlightedTripMatchKeySet]);

  const highlightedTripShiftIds = React.useMemo((): Set<string> => {
    const set = new Set<string>();
    matchingActivitySet.forEach((act) => {
      const raw = (act as Record<string, unknown>).vehicle_shift_id ?? (act as Record<string, unknown>).vehicleShiftId;
      if (typeof raw === 'string' && raw.trim() !== '') set.add(raw.trim());
    });
    return set;
  }, [matchingActivitySet]);
  const buildMapTripInfoPanelData = React.useCallback((params: {
    markerType: 'pickup' | 'dropoff' | 'other';
    activity?: SimulationActivity | null;
    ridePoint?: Record<string, unknown> | null;
    matchKeys: string[];
  }): MapTripInfoPanelData => {
    const { markerType, activity, ridePoint, matchKeys } = params;
    const matchKeysSet = new Set(matchKeys);
    const matchedRidePoint =
      ridePoint ??
      ((ridePoints?.find((p) => ridePointMatchesTripKeys(p as Record<string, unknown>, matchKeysSet)) as
        | Record<string, unknown>
        | undefined) ??
        null);

    const ridePointPassengers = matchedRidePoint?.passengers;
    const passengersFromRidePoint = Array.isArray(ridePointPassengers) ? ridePointPassengers.length : null;
    const aRec = (activity as Record<string, unknown> | null | undefined) ?? null;
    const pickCount = typeof aRec?.pickup_count === 'number' ? aRec.pickup_count : null;
    const dropCount = typeof aRec?.dropoff_count === 'number' ? aRec.dropoff_count : null;
    const usersPickedRaw = aRec?.users_picked_up;
    const usersDroppedRaw = aRec?.users_dropped_off;
    const usersPicked = Array.isArray(usersPickedRaw) ? usersPickedRaw.length : null;
    const usersDropped = Array.isArray(usersDroppedRaw) ? usersDroppedRaw.length : null;
    const passengers =
      passengersFromRidePoint ??
      (markerType === 'pickup' ? pickCount ?? usersPicked : dropCount ?? usersDropped) ??
      null;

    const vehicleLabel =
      (activity ? vehicleKeyToLabel[getVehicleKey(activity)] : null) ??
      (activity?.vehicle_id ? String(activity.vehicle_id) : null) ??
      'â€”';
    const tripId =
      (activity ? tripIdFromActivity(activity) : null) ??
      normalizeTripId(matchedRidePoint?.ride_id) ??
      normalizeTripId(matchedRidePoint?.request_id) ??
      matchKeys[0] ??
      'â€”';
    const activitiesList = selectedSim?.activities ?? [];
    const keySet = new Set(matchKeys);
    const matchingActivities = activitiesList.filter((a) =>
      collectActivityIdCandidates(a).some((k) => keySet.has(k))
    );

    const pickupActivityMs = matchingActivities
      .filter((a) => activityStopKindForUi(a) === 'pickup')
      .map((a) =>
        parseValidIsoMs(a.activity_start_time) ??
        parseValidIsoMs(a.activity_start) ??
        parseValidIsoMs(a.min_activity_start_time) ??
        parseValidIsoMs(a.start_time) ??
        parseValidIsoMs(a.time)
      )
      .filter((v): v is number => v != null);
    const dropoffActivityMs = matchingActivities
      .filter((a) => activityStopKindForUi(a) === 'dropoff')
      .map((a) =>
        parseValidIsoMs((a as Record<string, unknown>).activity_end_time) ??
        parseValidIsoMs(a.activity_start_time) ??
        parseValidIsoMs(a.activity_start) ??
        parseValidIsoMs(a.min_activity_start_time) ??
        parseValidIsoMs(a.start_time) ??
        parseValidIsoMs(a.time)
      )
      .filter((v): v is number => v != null);
    const pickupActivity =
      matchingActivities.find((a) => activityStopKindForUi(a) === 'pickup') ??
      (markerType === 'pickup' ? activity ?? null : null);
    const dropoffActivity =
      matchingActivities.find((a) => activityStopKindForUi(a) === 'dropoff') ??
      (markerType === 'dropoff' ? activity ?? null : null);

    const pickupTripMs: number[] = [];
    const dropoffTripMs: number[] = [];
    const requestTimeFromTrips: string[] = [];
    for (const act of matchingActivities) {
      const trips = (act as Record<string, unknown>).trips;
      if (!Array.isArray(trips)) continue;
      for (const item of trips) {
        if (item == null || typeof item !== 'object') continue;
        const trip = item as Record<string, unknown>;
        const at = (trip.activity_type ?? '').toString().toUpperCase();
        const tripIds = [
          normalizeTripId(trip.rider_ride_id),
          normalizeTripId((trip.ride as Record<string, unknown> | undefined)?.ride_id),
          normalizeTripId((trip.ride as Record<string, unknown> | undefined)?.external_reference_id),
          normalizeTripId((trip.ride as Record<string, unknown> | undefined)?.request_id),
        ].filter((x): x is string => x != null);
        if (tripIds.length > 0 && !tripIds.some((id) => keySet.has(id))) continue;

        const ride = trip.ride as Record<string, unknown> | undefined;
        const req = ride?.request_time;
        if (typeof req === 'string' && req.trim() !== '') requestTimeFromTrips.push(req);

        if (at === 'PICK') {
          const t = parseValidIsoMs(trip.activity_start_time) ?? parseValidIsoMs(trip.activity_end_time);
          if (t != null) pickupTripMs.push(t);
        } else if (at === 'DROP') {
          const t = parseValidIsoMs(trip.activity_end_time) ?? parseValidIsoMs(trip.activity_start_time);
          if (t != null) dropoffTripMs.push(t);
        }
      }
    }

    const requestTime = firstDefinedString([
      matchedRidePoint?.request_time,
      matchedRidePoint?.requested_time,
      ...requestTimeFromTrips,
      activity?.request_time,
      activity?.requested_time,
      (activity as Record<string, unknown> | undefined)?.requested_pickup_time,
    ]) ?? 'â€”';
    const pickupMs = pickupTripMs.length ? Math.min(...pickupTripMs) : (pickupActivityMs.length ? Math.min(...pickupActivityMs) : null);
    const dropoffMs = dropoffTripMs.length ? Math.max(...dropoffTripMs) : (dropoffActivityMs.length ? Math.max(...dropoffActivityMs) : null);
    const pickupTime = pickupMs != null ? new Date(pickupMs).toISOString() : 'â€”';
    const dropoffTime = dropoffMs != null ? new Date(dropoffMs).toISOString() : 'â€”';

    const travel = resolveTripTravelDisplay(matchKeys, activitiesList);
    const passengerUserId = passengerUserIdFromActivitiesAndRidePoint(
      matchKeys,
      activitiesList,
      matchedRidePoint ?? undefined
    );

    const rp = matchedRidePoint as Record<string, unknown> | null | undefined;
    let mapsFromLat = coerceRideCoordinate(rp?.from_latitude) ?? undefined;
    let mapsFromLng = coerceRideCoordinate(rp?.from_longitude) ?? undefined;
    let mapsToLat = coerceRideCoordinate(rp?.to_latitude) ?? undefined;
    let mapsToLng = coerceRideCoordinate(rp?.to_longitude) ?? undefined;
    const mapsFromAddress =
      resolveLegAddress({
        leg: 'pickup',
        ridePoint: rp ?? undefined,
        activity: pickupActivity,
        fallbackToStopAddress: true,
        fallbackToCoords: false,
      }) ??
      (typeof rp?.from_address === 'string' ? rp.from_address.trim() || undefined : undefined);
    const mapsToAddress =
      resolveLegAddress({
        leg: 'dropoff',
        ridePoint: rp ?? undefined,
        activity: dropoffActivity,
        fallbackToStopAddress: true,
        fallbackToCoords: false,
      }) ??
      (typeof rp?.to_address === 'string' ? rp.to_address.trim() || undefined : undefined);

    const inferred = inferMapsEndpointsFromMatchingActivities(
      matchingActivities,
      mapsFromLat,
      mapsFromLng,
      mapsToLat,
      mapsToLng
    );
    mapsFromLat = inferred.mapsFromLat;
    mapsFromLng = inferred.mapsFromLng;
    mapsToLat = inferred.mapsToLat;
    mapsToLng = inferred.mapsToLng;

    let identifierRows = buildTripPanelIdentifierRowsFromRidePoint(matchedRidePoint ?? undefined, t);
    if (identifierRows.length === 0 && activity) {
      identifierRows = buildTripPanelIdentifierRowsFromRidePoint(activity as unknown as Record<string, unknown>, t);
    }

    return {
      tripId,
      requestTime,
      pickupStopTime: pickupTime,
      dropoffStopTime: dropoffTime,
      totalTravelTime: travel.formatted,
      travelTimeTooltip: travel.tooltip,
      passengers: passengers != null ? String(passengers) : 'â€”',
      passengerUserId,
      pickupVehicle: vehicleLabel,
      isUnallocated: false,
      mapsFromLat,
      mapsFromLng,
      mapsToLat,
      mapsToLng,
      mapsFromAddress,
      mapsToAddress,
      identifierRows,
    };
  }, [ridePoints, vehicleKeyToLabel, selectedSim?.activities, t]);

  const handleHighlightActivity = React.useCallback(
    (activity: SimulationActivity | null) => {
      if (activity == null) {
        setHighlightedActivity(null);
        setHighlightedTripRideId(null);
        setHighlightedTripMatchKeys([]);
        setHighlightedUnmatchedRide(null);
        setMapTripInfoPanelData(null);
        return;
      }
      setHighlightedUnmatchedRide(null);
      const seed = deriveTripSeedFast(activity, ridePointCoordIndex);
      const expanded = expandTripMatchKeysFromIndex(seed, tripIdIndex);
      const matchKeys = expanded.length ? expanded : seed;
      setHighlightedTripMatchKeys(matchKeys);
      const tid = tripIdFromActivity(activity) ?? expanded[0] ?? seed[0] ?? null;
      setHighlightedTripRideId(tid);
      const coords = getActivityCoordinates(activity);
      if (coords) {
        setHighlightedActivity({
          lat: coords.lat,
          lng: coords.lng,
          activityId: activity.ride_id ?? activity.request_id,
        });
      } else {
        setHighlightedActivity(null);
      }

      const stopKind = activityStopKindForUi(activity);
      React.startTransition(() => {
        setMapTripInfoPanelData(
          buildMapTripInfoPanelData({
            markerType: stopKind === 'pickup' ? 'pickup' : stopKind === 'dropoff' ? 'dropoff' : 'other',
            activity,
            matchKeys,
          })
        );
      });
    },
    [tripIdIndex, ridePointCoordIndex, buildMapTripInfoPanelData]
  );

  const buildMapTripInfoPanelDataFromUnmatchedRide = React.useCallback((ride: UnmatchedRide): MapTripInfoPanelData => {
    const tripId =
      normalizeTripId(ride.ride_id) ??
      normalizeTripId(ride.request_id) ??
      normalizeTripId(ride.external_reference_id) ??
      'â€”';
    const requestTime = ride.request_time != null ? String(ride.request_time) : 'â€”';
    const pax = Array.isArray(ride.passengers) ? ride.passengers.length : 'â€”';
    const passengerUserId =
      normalizeTripId(ride.external_user_reference_id) ??
      normalizeTripId(ride.user_id) ??
      'â€”';
    return {
      tripId,
      requestTime,
      pickupStopTime: EM_DASH,
      dropoffStopTime: EM_DASH,
      totalTravelTime: 'â€”',
      travelTimeTooltip: TRAVEL_TIME_TOOLTIP_MISSING,
      passengers: typeof pax === 'number' ? String(pax) : pax,
      passengerUserId,
      pickupVehicle: t('simResultsMenu.tripPanel.unallocatedVehicle'),
      isUnallocated: true,
      mapsFromLat: coerceRideCoordinate(ride.from_latitude) ?? undefined,
      mapsFromLng: coerceRideCoordinate(ride.from_longitude) ?? undefined,
      mapsToLat: coerceRideCoordinate(ride.to_latitude) ?? undefined,
      mapsToLng: coerceRideCoordinate(ride.to_longitude) ?? undefined,
      mapsFromAddress: typeof ride.from_address === 'string' ? ride.from_address.trim() || undefined : undefined,
      mapsToAddress: typeof ride.to_address === 'string' ? ride.to_address.trim() || undefined : undefined,
      identifierRows: buildTripPanelIdentifierRowsFromUnmatchedRide(ride),
    };
  }, [t]);

  const handleSelectUnmatchedRide = React.useCallback(
    (ride: UnmatchedRide | null) => {
      if (ride == null) {
        setHighlightedUnmatchedRide(null);
        setMapTripInfoPanelData(null);
        return;
      }
      setUnallocatedVisible(true);
      setHighlightedTripRideId(null);
      setHighlightedTripMatchKeys([]);
      setHighlightedActivity(null);
      setHighlightedUnmatchedRide(ride);
      setMapTripInfoPanelData(buildMapTripInfoPanelDataFromUnmatchedRide(ride));
    },
    [buildMapTripInfoPanelDataFromUnmatchedRide]
  );

  const handleSelectActivityMarker = React.useCallback(
    (activity: SimulationActivity, markerType: 'pickup' | 'dropoff' | 'other') => {
      const tid = tripIdFromActivity(activity);
      setHighlightedUnmatchedRide(null);
      const seed = deriveTripSeedFast(activity, ridePointCoordIndex);
      const expanded = expandTripMatchKeysFromIndex(seed, tripIdIndex);
      const mk = expanded.length ? expanded : seed;
      setHighlightedTripMatchKeys(mk);
      setHighlightedTripRideId(tid ?? expanded[0] ?? seed[0] ?? null);
      setHighlightedActivity(null);
      React.startTransition(() => {
        setMapTripInfoPanelData(
          buildMapTripInfoPanelData({
            markerType: markerType === 'dropoff' ? 'dropoff' : markerType === 'pickup' ? 'pickup' : 'other',
            activity,
            matchKeys: mk,
          })
        );
      });
    },
    [ridePointCoordIndex, tripIdIndex, buildMapTripInfoPanelData]
  );

  const handleSelectRidePoint = React.useCallback(
    (p: Record<string, unknown>, markerType: 'pickup' | 'dropoff') => {
      setHighlightedUnmatchedRide(null);
      const seed = collectRidePointIdCandidates(p);
      const expanded = expandTripMatchKeysFromIndex(seed, tripIdIndex);
      const mk = expanded.length ? expanded : seed;
      setHighlightedTripMatchKeys(mk);
      setHighlightedTripRideId(
        normalizeTripId(p.ride_id) ?? normalizeTripId(p.request_id) ?? expanded[0] ?? seed[0] ?? null
      );
      setHighlightedActivity(null);
      React.startTransition(() => {
        setMapTripInfoPanelData(
          buildMapTripInfoPanelData({
            markerType,
            ridePoint: p,
            matchKeys: mk,
          })
        );
      });
    },
    [tripIdIndex, buildMapTripInfoPanelData]
  );

  const handleUnallocatedVisibleChange = React.useCallback((visible: boolean) => {
    setUnallocatedVisible(visible);
    if (!visible) {
      setHighlightedUnmatchedRide(null);
      setMapTripInfoPanelData((d) => (d?.isUnallocated ? null : d));
    }
  }, []);

  const {
    simulationName,
    simulationId,
    numberOfVehicles,
    numberOfRides,
    timeSettingsPreset,
    stepStatuses,
    status,
    simulationError,
    simulationHistory,
    setSimulationHistory,
    runQueue,
    cancelQueuedRuns,
  } = useSimulation();

  const selectedRunId = resultsCtx ? resultsCtx.selectedRunId : selectedRunIdLocal;

  React.useEffect(() => {
    setHighlightedTripRideId(null);
    setHighlightedTripMatchKeys([]);
    setHighlightedActivity(null);
    setHighlightedUnmatchedRide(null);
    setMapTripInfoPanelData(null);
  }, [selectedRunId]);

  const setSelectedRunId = React.useCallback(
    (runId: string | null) => {
      if (resultsCtx) resultsCtx.setSelectedRunId(runId);
      setSelectedRunIdLocal(runId);
    },
    [resultsCtx]
  );

  const simulationOptionsForSelect = React.useMemo(
    () => mergeResultsWithHistoryForSelect(simulationResults, simulationHistory),
    [simulationResults, simulationHistory]
  );

  const isResultsReady = React.useMemo(
    () =>
      status === 'finished' &&
      !!simulationId &&
      simulationHistory.some((sim) => sim.run_id === simulationId),
    [status, simulationId, simulationHistory]
  );

  // Refs to avoid recreating loadDetailedSimulationData when context/state updates after load (prevents repeated API calls)
  const resultsCtxRef = React.useRef(resultsCtx);
  const simulationResultsRef = React.useRef(simulationResults);
  const simulationHistoryRef = React.useRef(simulationHistory);
  const selectedRunIdRef = React.useRef(selectedRunId);
  /** Prevents re-running "select finished run" every time the user picks another run from the dropdown. */
  const lastAutoSelectedFinishedRunIdRef = React.useRef<string | null>(null);
  /** Monotonic id for in-flight detailed loads so we only clear the spinner when the latest request finishes. */
  const detailedLoadGenerationRef = React.useRef(0);
  /** In-session cache of detailed results per run — prevents re-fetching when switching between previously-loaded runs. */
  const detailedDataCacheRef = React.useRef<Map<string, {
    sim: SimulationRunSummary;
    ridePoints: any[] | null;
    vehicleRoutes: AddressPairRoute[];
    routeGenerationSummary: { total: number; succeeded: number; failed: number } | null;
  }>>(new Map());
  /** Deduplicate automatic /paths background loads (one attempt per run per session). */
  const attemptedRoadRoutePrefetchRunIdsRef = React.useRef<Set<string>>(new Set());
  resultsCtxRef.current = resultsCtx;
  simulationResultsRef.current = simulationResults;
  simulationHistoryRef.current = simulationHistory;
  selectedRunIdRef.current = selectedRunId;

  // Load simulation results from API (stable: use refs so effect that calls this doesn't re-run when context updates)
  const loadSimulationResults = React.useCallback(async () => {
    setLoadingResults(true);
    setResultsError(null);
    try {
      const results = await simulationService.fetchMyResults(authToken);
      const ordered = sortSimulationRunsNewestFirst(results);
      setSimulationResults(ordered);
      resultsCtxRef.current?.setResultsList(ordered);
      if (!selectedRunIdRef.current && results.length > 0) {
        setSelectedRunId(results[0].run_id);
      }
    } catch (err) {
      setResultsError(err instanceof Error ? err.message : t('simResultsMenu.errors.failedLoadResults'));
      console.error('Failed to load simulation results:', err);
    } finally {
      setLoadingResults(false);
    }
  }, [authToken]);

  const resolveResultsIdForRun = React.useCallback(
    async (runId: string): Promise<string> => {
      const fromMemory = resolveCanonicalResultsId(
        runId,
        selectedSim,
        simulationResultsRef.current,
        simulationHistoryRef.current
      );
      if (fromMemory) return fromMemory;

      // Also check the in-session detailed data cache (may have results_id from a prior load)
      const cachedDetail = detailedDataCacheRef.current.get(runId);
      if (cachedDetail?.sim.results_id) return cachedDetail.sim.results_id;

      // Only fetch the results list if we truly can't resolve from any local state.
      // The API often accepts run_id where results_id is expected, so this is a last resort.
      try {
        const latestResults = await simulationService.fetchMyResults(authToken);
        if (latestResults.length > 0) {
          const ordered = sortSimulationRunsNewestFirst(latestResults);
          setSimulationResults(ordered);
          resultsCtxRef.current?.setResultsList(ordered);
        }
        const fromLookup = latestResults.find((sim) => sim.run_id === runId)?.results_id;
        if (typeof fromLookup === 'string' && fromLookup.trim() !== '') return fromLookup;
      } catch (error) {
        console.warn('Unable to refresh results list for results_id lookup', error);
      }

      // Fall back to runId — the API often accepts run_id where results_id is expected.
      return runId;
    },
    [authToken, selectedSim]
  );

  const hasRestoredRef = React.useRef(false);
  React.useEffect(() => {
    if (!(open || isPage)) {
      hasRestoredRef.current = false;
      return;
    }
    if (!resultsCtx) return;
    const { resultsList, selectedRunId: ctxRunId, getCachedRunDetails } = resultsCtx;
    if (resultsList.length === 0) return;
    // Merge context list into local state without overwriting runs that already have detailed data locally
    setSimulationResults((prev) => {
      const byId = new Map<string, SimulationRunSummary>(prev.map((s) => [s.run_id, s]));
      resultsList.forEach((s) => {
        const existing = byId.get(s.run_id);
        if (!existing || !existing.metrics_fetched) byId.set(s.run_id, s);
      });
      const fromList = resultsList.map((s) => byId.get(s.run_id)!);
      const prevOnly = prev.filter((s) => !resultsList.some((r) => r.run_id === s.run_id));
      return sortSimulationRunsNewestFirst([...fromList, ...prevOnly]);
    });
    if (ctxRunId && getCachedRunDetails(ctxRunId)) {
      if (hasRestoredRef.current) return;
      hasRestoredRef.current = true;
      const cached = getCachedRunDetails(ctxRunId)!;
      setSelectedRunIdLocal(ctxRunId);
      setSelectedSim(cached.sim);
      setRidePoints(cached.ridePoints);
      setRidesError(cached.ridePoints?.length === 0 ? t('simResultsMenu.errors.noRidePointsForRun') : null);
      setDetailedDataError(null);
      setSimulationResults((prev) => {
        const has = prev.some((s) => s.run_id === cached.sim.run_id);
        if (has) return sortSimulationRunsNewestFirst(prev.map((s) => (s.run_id === cached.sim.run_id ? cached.sim : s)));
        return sortSimulationRunsNewestFirst([cached.sim, ...prev]);
      });
    } else {
      hasRestoredRef.current = false;
      if (ctxRunId) setSelectedRunIdLocal(ctxRunId);
    }
  // Depend only on the data fields this effect reads, not the whole context object.
  // resultsCtx itself recreates whenever runDetailsCache changes (setCachedRunDetails is called
  // later in this file), which would cause an infinite loop if used directly.
  }, [open, isPage, resultsCtx?.resultsList, resultsCtx?.selectedRunId, resultsCtx?.getCachedRunDetails]);

  // Load results list when drawer/page opens only if we don't have persisted list (data persists when navigating back to map)
  React.useEffect(() => {
    if (!(open || isPage)) return;
    if (resultsCtx && resultsCtx.resultsList.length > 0) return;
    loadSimulationResults();
  }, [open, isPage, resultsCtx?.resultsList.length, loadSimulationResults]);

  // Handle reload button click — clears the in-session detail cache so fresh data is fetched
  const handleReload = React.useCallback(() => {
    detailedDataCacheRef.current.clear();
    attemptedRoadRoutePrefetchRunIdsRef.current.clear();
    loadSimulationResults();
  }, [loadSimulationResults]);

  const handleClearTripFocus = React.useCallback(() => {
    setHighlightedTripRideId(null);
    setHighlightedTripMatchKeys([]);
    setHighlightedActivity(null);
    setHighlightedUnmatchedRide(null);
    setMapTripInfoPanelData(null);
  }, []);

  // Ensure the select list reflects local simulation history
  React.useEffect(() => {
    if (!(open || isPage)) return;
    if (!selectedRunId && simulationHistory.length > 0) {
      setSelectedRunId(simulationHistory[0].run_id);
    }
  }, [open, isPage, selectedRunId, simulationHistory]);

  const handleManualLoad = async () => {
    const trimmed = manualRunId.trim();
    if (!trimmed) return;
    setManualLoading(true);
    setManualError(null);
    try {
      // Validate the run ID exists by fetching metrics (single call)
      const metricsRow = await fetchSimulationMetrics(trimmed);
      if (!metricsRow) {
        throw new Error(t('simResultsMenu.errors.noMetricsForRunId'));
      }

      // Add a stub summary (without metrics_fetched) so the useEffect triggers
      // loadDetailedSimulationData which fetches everything in parallel.
      const stubSummary: SimulationRunSummary = {
        run_id: trimmed,
        scenario_name: metricsRow.scenario_name || t('simResultsMenu.errors.importedSimulationFallback'),
        run_time: metricsRow.run_time || new Date().toISOString(),
        results_id: (metricsRow as Record<string, unknown>).results_id as string | undefined,
        ...metricsRow,
      };

      setSimulationHistory(prev => [stubSummary, ...prev.filter(run => run.run_id !== trimmed)]);
      setSelectedRunId(trimmed);
      setManualRunId('');
    } catch (err) {
      setManualError(err instanceof Error ? err.message : t('simResultsMenu.errors.failedLoadRun'));
    } finally {
      setManualLoading(false);
    }
  };

  const prefetchRoadRoutesForRun = React.useCallback((runId: string, summary?: SimulationRunSummary | null) => {
    if (!runId || attemptedRoadRoutePrefetchRunIdsRef.current.has(runId)) return;
    attemptedRoadRoutePrefetchRunIdsRef.current.add(runId);

    const currentResults = simulationResultsRef.current;
    const currentHistory = simulationHistoryRef.current;
    const existingSummary =
      summary ||
      currentResults.find((sim) => sim.run_id === runId) ||
      currentHistory.find((sim) => sim.run_id === runId) ||
      null;
    const resultsId = resolveResultsId(runId, existingSummary);

    void fetchRoadRoutesByResultsIdWithMetadata(resultsId, { timeoutMs: 90000 })
      .then((roadRoutesData) => {
        const autoRoutes: AddressPairRoute[] = Object.entries(roadRoutesData)
          .filter((entry): entry is [string, RoadRouteWithMetadata] => entry[1] != null && entry[1].geometry != null)
          .map(([routeId, route]) => ({ routeId, geometry: route.geometry!, metadata: route.metadata }));
        const routeSummary =
          autoRoutes.length > 0
            ? { total: autoRoutes.length, succeeded: autoRoutes.length, failed: 0 }
            : null;

        const cachedCore = detailedDataCacheRef.current.get(runId);
        if (cachedCore) {
          detailedDataCacheRef.current.set(runId, {
            ...cachedCore,
            vehicleRoutes: autoRoutes,
            routeGenerationSummary: routeSummary,
          });
        }

        if (selectedRunIdRef.current !== runId) return;
        setVehicleRoutes(autoRoutes);
        if (routeSummary) setRouteGenerationSummary(routeSummary);
      })
      .catch((err) => {
        console.warn('Unable to fetch road routes for run', err);
      });
  }, []);

  // Load detailed data for selected simulation (stable callback: uses refs to avoid re-running effect when context updates after setCachedRunDetails)
  const loadDetailedSimulationData = React.useCallback(async (runId: string) => {
    // Check in-session cache first — instant restore, no API calls
    const cached = detailedDataCacheRef.current.get(runId);
    if (cached) {
      setSelectedSim(cached.sim);
      setRidePoints(cached.ridePoints);
      setVehicleRoutes(cached.vehicleRoutes);
      if (cached.routeGenerationSummary) setRouteGenerationSummary(cached.routeGenerationSummary);
      setRidesError(cached.ridePoints && cached.ridePoints.length === 0 ? t('simResultsMenu.errors.noRidePointsForRun') : null);
      setDetailedDataError(null);
      setLoadingDetailedData(false);
      const existingCached = resultsCtxRef.current?.getCachedRunDetails(runId);
      if (!existingCached) {
        resultsCtxRef.current?.setCachedRunDetails(runId, { sim: cached.sim, ridePoints: cached.ridePoints });
      }
      return;
    }

    const generation = ++detailedLoadGenerationRef.current;
    setLoadingDetailedData(true);
    setDetailedDataError(null);

    const results = simulationResultsRef.current;
    const history = simulationHistoryRef.current;
    const existingSummary =
      results.find(sim => sim.run_id === runId) ||
      history.find(sim => sim.run_id === runId) ||
      null;
    const resultsId = resolveResultsId(runId, existingSummary);

    try {
      // Fetch detailed metrics, solution (ride points + fallback activities), activities, vehicles, and unmatched rides.
      // Road routes (/paths) are fetched separately in the background so OD trips can render immediately.
      // fetchSolutionResults returns both ride points AND activities from a single /integration/solution call.
      // fetchSimulationActivities hits /results/{id}/activities which returns richer data (trips, user IDs, addresses),
      // so we use it as primary and fall back to solution-derived activities if it fails.
      // fetchSimulationVehicles brings back the `vehicle_id` ↔ `external_driver_id` mapping so we can stamp shift names
      // (e.g. "DAY 1 COACH") onto activities client-side — the activities endpoint itself doesn't echo our outbound
      // `shift_name` reliably across simulator versions, but the vehicles endpoint does echo external ids.
      const [metricsRow, activities, solutionResults, unmatchedRides, vehicleRows] = await Promise.allSettled([
        fetchSimulationMetrics(resultsId),
        fetchSimulationActivities(resultsId).catch(err => {
          console.warn('Unable to fetch activities for run', err);
          return undefined;
        }),
        fetchSolutionResults(resultsId).catch(err => {
          console.warn('Unable to fetch solution for run', err);
          return { ridePoints: null, activities: [] };
        }),
        fetchUnmatchedRides(resultsId).catch(err => {
          console.warn('Unable to fetch unmatched rides for run', err);
          return [] as UnmatchedRide[];
        }),
        fetchSimulationVehicles(resultsId).catch(err => {
          console.warn('Unable to fetch vehicles for shift-name join', err);
          return [];
        }),
      ]);

      const currentSelectedRunId = selectedRunIdRef.current;
      // Check if selection changed while loading (race condition prevention)
      if (currentSelectedRunId !== runId) {
        return; // User selected a different run, ignore this result
      }

      // Handle metrics result
      if (metricsRow.status === 'rejected' || !metricsRow.value) {
        throw new Error(metricsRow.status === 'rejected'
          ? metricsRow.reason?.message || t('simResultsMenu.errors.failedFetchMetrics')
          : t('simResultsMenu.errors.noMetricsForRunId'));
      }

      const metrics = metricsRow.value;
      const solutionData = solutionResults.status === 'fulfilled' ? solutionResults.value : { ridePoints: null, activities: [] };
      const unmatchedRidesData: UnmatchedRide[] | undefined =
        unmatchedRides.status === 'fulfilled' ? unmatchedRides.value : undefined;
      const vehicleRowsData = vehicleRows.status === 'fulfilled' ? vehicleRows.value : [];

      // Use /activities as primary (richer data); fall back to solution-derived activities
      const rawActivitiesData = (activities.status === 'fulfilled' && activities.value)
        ? activities.value
        : (solutionData.activities.length > 0 ? solutionData.activities : undefined);
      // Stamp shift_name / vehicle_name onto activities by joining server-side
      // `vehicle_id` / `vehicle_shift_id` to outbound `external_driver_id` /
      // `external_reference_id` (via /vehicles), then looking up the label in
      // the shift label registry. This is what surfaces "DAY 1 COACH" instead
      // of "Vehicle 1" in the results panel AND in the Optimised Fixed Route
      // Network drawer (which reads shift_name off activities).
      const activitiesData = rawActivitiesData
        ? stampShiftNamesOnActivities(rawActivitiesData, vehicleRowsData)
        : rawActivitiesData;

      // Use ride points from solution; fall back to deriving from activities
      const ridePointsData = solutionData.ridePoints;
      const combinedRidePoints =
        (ridePointsData && ridePointsData.length
          ? ridePointsData
          : buildRidePointsFromActivities(activitiesData || [])) || undefined;

      // Create detailed simulation summary
      const detailedSim: SimulationRunSummary = {
        run_id: runId,
        results_id: resultsId,
        ...existingSummary, // Preserve any existing summary fields
        ...metrics, // Override with detailed metrics
        scenario_name: metrics.scenario_name || existingSummary?.scenario_name || t('simResultsMenu.errors.unknownScenario'),
        run_time: metrics.run_time || existingSummary?.run_time || new Date().toISOString(),
        metrics_fetched: true,
        activities: activitiesData,
        ride_points: combinedRidePoints,
        unmatched_rides: unmatchedRidesData ?? existingSummary?.unmatched_rides,
      };

      // Double-check selection hasn't changed before updating state
      if (selectedRunIdRef.current === runId) {
        const points = combinedRidePoints || null;
        setSelectedSim(detailedSim);
        setRidePoints(points);
        setRidesError(
          points && points.length === 0
            ? t('simResultsMenu.errors.noRidePointsForRun')
            : null
        );
        setSimulationResults((prev) => {
          const has = prev.some((sim) => sim.run_id === runId);
          const next = has ? prev.map((sim) => (sim.run_id === runId ? detailedSim : sim)) : [detailedSim, ...prev];
          return sortSimulationRunsNewestFirst(next);
        });
        resultsCtxRef.current?.setResultsList((prev) => {
          const has = prev.some((sim) => sim.run_id === runId);
          const next = has ? prev.map((sim) => (sim.run_id === runId ? detailedSim : sim)) : [detailedSim, ...prev];
          return sortSimulationRunsNewestFirst(next);
        });
        resultsCtxRef.current?.setCachedRunDetails(runId, { sim: detailedSim, ridePoints: points });

        // Write core detail to in-session cache immediately so re-selecting this run is instant.
        // Road paths are attached to this cache entry once /paths completes in the background.
        detailedDataCacheRef.current.set(runId, {
          sim: detailedSim,
          ridePoints: points,
          vehicleRoutes: [],
          routeGenerationSummary: null,
        });
      }

      // Fetch /paths in the background after the core run data is already shown.
      // If this fails, we keep O-D lines available and simply leave road paths disabled.
      prefetchRoadRoutesForRun(runId, detailedSim);

    } catch (err) {
      const currentSelectedRunId = selectedRunIdRef.current;
      // Only show error if this is still the selected run
      if (currentSelectedRunId === runId) {
        const errorMessage = err instanceof Error ? err.message : t('simResultsMenu.errors.failedLoadDetailed');
        setDetailedDataError(errorMessage);
        console.error('Failed to load detailed simulation data:', err);
        
        const results = simulationResultsRef.current;
        const history = simulationHistoryRef.current;
        // Still try to show basic summary if available
        const existingSummary = 
          results.find(sim => sim.run_id === runId) ||
          history.find(sim => sim.run_id === runId) ||
          null;
        if (existingSummary) {
          setSelectedSim(existingSummary);
        }
      }
    } finally {
      if (generation === detailedLoadGenerationRef.current) {
        setLoadingDetailedData(false);
      }
    }
  }, [prefetchRoadRoutesForRun]);

  // Update selected simulation + ride points when selection changes
  React.useEffect(() => {
    if (!(open || isPage)) return;
    if (!selectedRunId) {
      setSelectedSim(null);
      setRidePoints(null);
      setRidesError(null);
      setDetailedDataError(null);
      return;
    }

    // Check if we already have detailed data.
    // `unmatched_rides: null` means "loaded and none unmatched" (valid),
    // while missing/undefined means "not loaded yet" and must trigger a fetch.
    const existingMatch =
      simulationResults.find(sim => sim.run_id === selectedRunId && sim.metrics_fetched) ||
      simulationHistory.find(sim => sim.run_id === selectedRunId && sim.metrics_fetched) ||
      null;
    const hasUnmatchedRidesLoaded =
      existingMatch != null &&
      Object.prototype.hasOwnProperty.call(existingMatch, 'unmatched_rides') &&
      existingMatch.unmatched_rides !== undefined;

    if (existingMatch && hasUnmatchedRidesLoaded) {
      const baseRidePoints = existingMatch?.ride_points || null;
      const derived =
        (baseRidePoints && baseRidePoints.length
          ? baseRidePoints
          : buildRidePointsFromActivities(existingMatch?.activities || [])) || null;
      setSelectedSim(existingMatch);
      setRidePoints(derived);
      setRidesError(
        derived && derived.length === 0
          ? t('simResultsMenu.errors.noRidePointsForRun')
          : null
      );
      setDetailedDataError(null);
      // Only update cache when missing to avoid triggering context updates and re-running effects
      if (!resultsCtxRef.current?.getCachedRunDetails(selectedRunId)) {
        resultsCtxRef.current?.setCachedRunDetails(selectedRunId, { sim: existingMatch, ridePoints: derived });
      }
      if (!detailedDataCacheRef.current.has(selectedRunId)) {
        detailedDataCacheRef.current.set(selectedRunId, {
          sim: existingMatch,
          ridePoints: derived,
          vehicleRoutes: [],
          routeGenerationSummary: null,
        });
      }
      prefetchRoadRoutesForRun(selectedRunId, existingMatch);
    } else {
      // Load detailed data from API
      loadDetailedSimulationData(selectedRunId);
    }
  }, [open, isPage, selectedRunId, simulationHistory, simulationResults, loadDetailedSimulationData, prefetchRoadRoutesForRun]);

  const timelineSource = React.useMemo(() => {
    if (ridePoints && ridePoints.length) return ridePoints;
    if (selectedSim?.activities && selectedSim.activities.length) {
      return selectedSim.activities
        .map((activity: any) => ({
          request_time:
            activity.activity_start_time ||
            activity.start_time ||
            activity.min_activity_start_time ||
            activity.activity_time,
        }))
        .filter(entry => !!entry.request_time);
    }
    return [];
  }, [ridePoints, selectedSim]);

  const tripsPerHourData = React.useMemo(
    () => binRequestsPerHour(timelineSource),
    [timelineSource],
  );

  // Auto-select the run once when a simulation finishes (per completed simulationId). Do not depend on
  // selectedRunId â€” otherwise every manual dropdown change re-runs this and forces selection back to simulationId.
  const vehicleIdsForRun = React.useMemo(() => {
    if (!selectedSim?.activities?.length) return [];
    const uniqueIds = new Set<string>();
    selectedSim.activities.forEach((activity) => {
      const vehicleId = getVehicleKey(activity);
      if (vehicleId) uniqueIds.add(vehicleId);
    });
    return Array.from(uniqueIds);
  }, [selectedSim?.activities]);

  // Pre-sort once per fleet change so filterRidePointsByVisibilityFast skips the per-point sort.
  const sortedVehicleIds = React.useMemo(
    () => [...vehicleIdsForRun].sort((a, b) => b.length - a.length),
    [vehicleIdsForRun]
  );

  // Map a route's metadata.vehicle_shift_id back to the visibility-map key produced by getVehicleKey().
  const shiftIdToVehicleKey = React.useMemo(() => {
    const m = new Map<string, string>();
    (selectedSim?.activities ?? []).forEach((activity) => {
      const a = activity as Record<string, unknown>;
      const sid = (a.vehicle_shift_id ?? a.shift_id) as string | number | undefined;
      if (sid == null || sid === '') return;
      m.set(String(sid), getVehicleKey(activity));
    });
    return m;
  }, [selectedSim?.activities]);

  // (vehicle_shift_id, driver_activity_number) -> { startMs, endMs }.
  // Activities expose min/max driver_activity_number ranges (a single activity row may span
  // several numbered events); register each integer in the range so route metadata's
  // from/to_driver_activity_number can be looked up directly.
  const shiftActivityTimeMs = React.useMemo(() => {
    type Bounds = { startMs: number; endMs: number };
    const m = new Map<string, Map<number, Bounds>>();
    (selectedSim?.activities ?? []).forEach((activity) => {
      const a = activity as Record<string, unknown>;
      const sid = (a.vehicle_shift_id ?? a.shift_id) as string | number | undefined;
      if (sid == null || sid === '') return;
      const startMs = parseRequestTimeToMs(typeof a.activity_start_time === 'string' ? a.activity_start_time : undefined);
      if (startMs == null) return;
      const endMs = parseRequestTimeToMs(typeof a.activity_end_time === 'string' ? a.activity_end_time : undefined) ?? startMs;
      const key = String(sid);
      let inner = m.get(key);
      if (!inner) { inner = new Map(); m.set(key, inner); }

      const setNum = (num: number) => { inner!.set(num, { startMs, endMs }); };

      const single = toFiniteNumber(a.driver_activity_number);
      if (single != null) setNum(single);

      const minN = toFiniteNumber(a.min_driver_activity_number);
      const maxN = toFiniteNumber(a.max_driver_activity_number);
      if (minN != null && maxN != null) {
        const lo = Math.min(minN, maxN);
        const hi = Math.max(minN, maxN);
        for (let n = lo; n <= hi; n += 1) setNum(n);
      } else if (minN != null) {
        setNum(minN);
      } else if (maxN != null) {
        setNum(maxN);
      }
    });
    return m;
  }, [selectedSim?.activities]);

  const visibleRidePoints = React.useMemo(
    () => filterRidePointsByVisibilityFast(ridePoints ?? [], vehicleVisibilityMap, sortedVehicleIds),
    [ridePoints, vehicleVisibilityMap, sortedVehicleIds]
  );

  React.useEffect(() => {
    if (!selectedRunId) {
      setVehicleVisibilityMap({});
      return;
    }
    if (vehicleIdsForRun.length === 0) {
      setVehicleVisibilityMap({});
      return;
    }
    setVehicleVisibilityMap(() =>
      vehicleIdsForRun.reduce<Record<string, boolean>>((next, vehicleId) => {
        next[vehicleId] = true;
        return next;
      }, {})
    );
  }, [selectedRunId, vehicleIdsForRun]);

  const handleVehicleVisibilityChange = React.useCallback((vehicleId: string, visible: boolean) => {
    setVehicleVisibilityMap((prev) => ({ ...prev, [vehicleId]: visible }));
  }, []);

  const handleShowAllVehicles = React.useCallback(() => {
    setVehicleVisibilityMap((prev) => {
      const next: Record<string, boolean> = {};
      Object.keys(prev).forEach((vehicleId) => { next[vehicleId] = true; });
      vehicleIdsForRun.forEach((vehicleId) => { next[vehicleId] = true; });
      return next;
    });
  }, [vehicleIdsForRun]);

  const handleHideAllVehicles = React.useCallback(() => {
    setVehicleVisibilityMap((prev) => {
      const next: Record<string, boolean> = {};
      Object.keys(prev).forEach((vehicleId) => { next[vehicleId] = false; });
      vehicleIdsForRun.forEach((vehicleId) => { next[vehicleId] = false; });
      return next;
    });
  }, [vehicleIdsForRun]);

  // Auto-select the most recent run when a simulation finishes
  React.useEffect(() => {
    if (!(open || isPage)) return;
    if (status !== 'finished' || !simulationId) return;
    if (!simulationHistory.some((sim) => sim.run_id === simulationId)) return;
    if (lastAutoSelectedFinishedRunIdRef.current === simulationId) return;
    lastAutoSelectedFinishedRunIdRef.current = simulationId;
    setSelectedRunId(simulationId);
  }, [open, isPage, status, simulationId, simulationHistory, setSelectedRunId]);

  // Initialize timeline to end of ride data so the default 24h window shows the full day highlighted
  React.useEffect(() => {
    if (!selectedRunId || !ridePoints?.length) {
      setTimelineTime(null);
      return;
    }
    const range = getRidePointsTimeRange(ridePoints);
    if (range) setTimelineTime(new Date(range.maxMs));
  }, [selectedRunId, ridePoints?.length]);

  // Reset generated road routes when the selected run changes (not on timeline scrub â€” keep /paths geometry visible).
  React.useEffect(() => {
    setRoadRoutesByRideId({});
    setVehicleRoutes([]);
    setRouteGenerationSummary(null);
  }, [selectedRunId]);

  const handleGenerateRoadRoutes = React.useCallback(async () => {
    if (isGeneratingRoadRoutes || !selectedRunId) return;

    setIsGeneratingRoadRoutes(true);
    setRouteFailureWarning(null);

    try {
      const resolvedResultsId = await resolveResultsIdForRun(selectedRunId);
      const candidateIds = Array.from(
        new Set(
          [resolvedResultsId, selectedRunId].filter(
            (value): value is string => typeof value === 'string' && value.trim() !== ''
          )
        )
      );

      if (candidateIds.length === 0) {
        setRouteFailureWarning(t('simResultsMenu.errors.unableGenerateRoadRoutesNoId'));
        return;
      }

      // routesByVehicleId is keyed by driver_ride_id (one entry per vehicle segment).
      let routesByVehicleId: RoadRouteByRideId = {};
      for (const candidateId of candidateIds) {
        routesByVehicleId = await fetchRoadRoutesByResultsIdWithMetadata(candidateId, { timeoutMs: 90000 });
        if (Object.keys(routesByVehicleId).length > 0) break;
      }

      // Build the vehicle route list â€” one polyline per vehicle.
      const routes: AddressPairRoute[] = Object.entries(routesByVehicleId)
        .filter((entry): entry is [string, RoadRouteWithMetadata] => entry[1] != null && entry[1].geometry != null)
        .map(([routeId, route]) => ({ routeId, geometry: route.geometry!, metadata: route.metadata }));

      setVehicleRoutes(routes);
      setRouteGenerationSummary({
        total: routes.length,
        succeeded: routes.length,
        failed: 0,
      });

      if (routes.length === 0) {
        setRouteFailureWarning(t('simResultsMenu.errors.noRoadRoutesReturned'));
      }
    } finally {
      setIsGeneratingRoadRoutes(false);
    }
  }, [isGeneratingRoadRoutes, resolveResultsIdForRun, selectedRunId]);
  void handleGenerateRoadRoutes;

  // Add to Map handler
  const handleAddToMap = React.useCallback(async () => {
    if (!context || !ridePoints || ridePoints.length === 0 || isAddingOnDemandToMap) return;
    const { global } = context;
    const { setLayers, setActiveLayerId, idCounter, setIdCounter, mapLayers, setFleets } = global;

    setIsAddingOnDemandToMap(true);
    setAddToMapProgressText(t('simResultsMenu.addToMap.preparingTrips'));
    await yieldToPaint();

    try {
      let resolvedLayerId = idCounter;
      const shapes: Array<Line | Point> = [];
      type ImportedTripLine = Line & {
        originPointId: string;
        destPointId: string;
      };
      const tripLinesWithPointLinks: ImportedTripLine[] = [];
      const dedupedRidePoints = (() => {
        const seen = new Set<string>();
        const out: typeof ridePoints = [];
        for (const rp of ridePoints) {
          const rec = rp as Record<string, unknown>;
          const idKey =
            (typeof rec.ride_id === 'string' && rec.ride_id.trim()) ||
            (typeof rec.request_id === 'string' && rec.request_id.trim()) ||
            '';
          const fromLat = coerceRideCoordinate(rec.from_latitude);
          const fromLng = coerceRideCoordinate(rec.from_longitude);
          const toLat = coerceRideCoordinate(rec.to_latitude);
          const toLng = coerceRideCoordinate(rec.to_longitude);
          const reqTime = normalizeImportedRideRequestTime(rec.request_time);
          const coordKey =
            fromLat != null && fromLng != null && toLat != null && toLng != null
              ? `${fromLat},${fromLng}->${toLat},${toLng}`
              : 'no-coords';
          const key = idKey ? `trip:${idKey}` : `shape:${coordKey}|${reqTime}`;
          if (seen.has(key)) continue;
          seen.add(key);
          out.push(rp);
        }
        return out;
      })();
      const total = dedupedRidePoints.length;
      let importedFleetId: string | null = null;

      for (let idx = 0; idx < dedupedRidePoints.length; idx++) {
        const p = dedupedRidePoints[idx] as Record<string, unknown>;
        const fromLat = coerceRideCoordinate(p.from_latitude);
        const fromLng = coerceRideCoordinate(p.from_longitude);
        const toLat = coerceRideCoordinate(p.to_latitude);
        const toLng = coerceRideCoordinate(p.to_longitude);
        if (fromLat == null || fromLng == null || toLat == null || toLng == null) continue;

        const passengers = Array.isArray(p.passengers) ? p.passengers as Array<Record<string, unknown>> : [];
        const standardSeats = passengers
          .filter((passenger) => String(passenger.type ?? passenger.passenger_type ?? 'standard').toLowerCase() !== 'wheelchair')
          .reduce((sum, passenger) => sum + (Number(passenger.number_of_passengers ?? passenger.count ?? 1) || 0), 0);
        const wheelchairSeats = passengers
          .filter((passenger) => String(passenger.type ?? passenger.passenger_type ?? '').toLowerCase() === 'wheelchair')
          .reduce((sum, passenger) => sum + (Number(passenger.number_of_passengers ?? passenger.count ?? 1) || 0), 0);
        const rideId = typeof p.ride_id === 'string' && p.ride_id.trim() !== ''
          ? p.ride_id.trim()
          : `simulation-ride-${idx + 1}`;
        const normalizedRequestTime = normalizeImportedRideRequestTime(p.request_time);
        const normalizedRequestType = normalizeImportedRideRequestType(p.request_type);

        const originPointId = `sim-trip-origin-${uuidv4()}`;
        const destPointId = `sim-trip-dest-${uuidv4()}`;
        const originPoint: Point = {
          id: originPointId,
          layerId: resolvedLayerId,
          type: 'point',
          name: t('simResultsMenu.addToMap.tripOriginName', { rideId }),
          description: '',
          colour: '#FF0000',
          icon: 'location',
          size: 24,
          isLocked: false,
          coordinates: [fromLat, fromLng],
        };
        const destinationPoint: Point = {
          id: destPointId,
          layerId: resolvedLayerId,
          type: 'point',
          name: t('simResultsMenu.addToMap.tripDestinationName', { rideId }),
          description: '',
          colour: '#00FF00',
          icon: 'location',
          size: 24,
          isLocked: false,
          coordinates: [toLat, toLng],
        };
        const line: ImportedTripLine = {
          id: uuidv4(),
          layerId: resolvedLayerId,
          type: 'line',
          name: t('simResultsMenu.addToMap.tripLineName', { rideId }),
          colour: '#0000FF',
          description: t('simResultsMenu.addToMap.tripLineDescription', { rideId }),
          opacity: '0.85',
          borderColour: '#0000FF',
          borderWidth: 1.5,
          isLocked: true,
          coordinates: [
            [fromLat, fromLng],
            [toLat, toLng],
          ],
          dashPattern: 'none',
          arrowStyle: 'none',
          ride_id: rideId,
          user_id: typeof p.user_id === 'string' ? p.user_id : undefined,
          request_time: normalizedRequestTime || null,
          request_type: normalizedRequestType,
          standard_seat: standardSeats > 0 ? standardSeats : 1,
          wheelchair: wheelchairSeats,
          numPassengers: Math.max(1, standardSeats + wheelchairSeats),
          from_address: typeof p.from_address === 'string' ? p.from_address : undefined,
          to_address: typeof p.to_address === 'string' ? p.to_address : undefined,
          originPointId,
          destPointId,
        };
        tripLinesWithPointLinks.push(line);
        shapes.push(originPoint, destinationPoint, line);

        if (idx > 0 && idx % 500 === 0) {
          setAddToMapProgressText(t('simResultsMenu.addToMap.preparedProgress', { current: idx.toLocaleString(), total: total.toLocaleString() }));
          await yieldToPaint();
        }
      }

      setAddToMapProgressText(t('simResultsMenu.addToMap.addingTripsToMap', { total: shapes.length.toLocaleString() }));
      await yieldToPaint();

      const visibleTripPointIds = new Set<string>();
      tripLinesWithPointLinks.slice(0, TRIP_RENDER_CAP).forEach((line) => {
        visibleTripPointIds.add(line.originPointId);
        visibleTripPointIds.add(line.destPointId);
      });

      const newLayer: Layer = {
        layerId: resolvedLayerId,
        layerName: selectedSim?.scenario_name ? t('simResultsMenu.addToMap.tripLayerNameWithScenario', { name: selectedSim.scenario_name }) : t('simResultsMenu.addToMap.tripLayerNameDefault', { index: resolvedLayerId + 1 }),
        hidden: false,
        renderAll: false,
        shapes: shapes.map((shape) => {
          if (shape.type !== 'point') return shape;
          return {
            ...shape,
            hidden: !visibleTripPointIds.has(shape.id),
          };
        }),
      };

      setLayers((prevLayers) => {
        const maxLayerId = prevLayers.reduce((max, layer) => Math.max(max, layer.layerId), -1);
        resolvedLayerId = Math.max(idCounter, maxLayerId + 1);
        const layerWithResolvedId: Layer = {
          ...newLayer,
          layerId: resolvedLayerId,
          layerName: selectedSim?.scenario_name
            ? t('simResultsMenu.addToMap.tripLayerNameWithScenario', { name: selectedSim.scenario_name })
            : t('simResultsMenu.addToMap.tripLayerNameDefault', { index: resolvedLayerId + 1 }),
          shapes: newLayer.shapes.map((shape) => ({ ...shape, layerId: resolvedLayerId })),
        };
        return [...prevLayers, layerWithResolvedId];
      });
      setActiveLayerId(resolvedLayerId);
      setIdCounter((prevCounter) => Math.max(prevCounter, resolvedLayerId + 1));

      setAddToMapProgressText(t('simResultsMenu.addToMap.buildingFleet'));
      await yieldToPaint();

      let fleetSummary = ` ${t('simResultsMenu.addToMap.fleetNotCreated')}`;
      let fleetWarnings: string[] = [];
      try {
        if (!selectedSim?.run_id) {
          throw new Error(t('simResultsMenu.errors.noRunSelectedForFleet'));
        }
        const resolvedResultsId = await resolveResultsIdForRun(selectedSim.run_id);
        const [vehicleRows, settings] = await Promise.all([
          fetchSimulationVehicles(resolvedResultsId),
          fetchSimulationSettings(resolvedResultsId).catch(() => null),
        ]);
        const allPointShapes = mapLayers
          .flatMap((layer) => layer.shapes)
          .filter((shape): shape is Point => shape.type === 'point');
        const transform = transformResultsVehiclesToFleet({
          rows: vehicleRows,
          runId: selectedSim?.run_id ?? 'unknown-run',
          scenarioName: selectedSim?.scenario_name,
          serviceDate: settings?.run_time ?? null,
          existingPoints: allPointShapes,
          targetLayerId: resolvedLayerId,
        });
        fleetWarnings = transform.warnings;

        if (transform.depotPoints.length > 0) {
          setLayers((prevLayers) =>
            prevLayers.map((layer) =>
              layer.layerId === resolvedLayerId
                ? { ...layer, shapes: [...layer.shapes, ...transform.depotPoints.map((p) => ({ ...p, layerId: resolvedLayerId }))] }
                : layer
            )
          );
        }

        if (transform.fleet) {
          const fleetToImport = transform.fleet as Fleet;
          importedFleetId = fleetToImport.id;
          setFleets((prevFleets: Fleet[]) => {
            const merged = mergeImportedFleet(prevFleets, fleetToImport);
            if (merged.replacedEmptyFleet && merged.fleets[0]?.id) {
              importedFleetId = merged.fleets[0].id;
            }
            return merged.fleets;
          });
          fleetSummary = ` ${t('simResultsMenu.addToMap.fleetCreated', { total: transform.vehiclesCreated.toLocaleString() })}`;
        } else if (fleetWarnings.length > 0) {
          fleetSummary = ` ${fleetWarnings[0]}`;
        }
      } catch (fleetError) {
        fleetSummary = ` ${t('simResultsMenu.errors.fleetImportWarning', { message: fleetError instanceof Error ? fleetError.message : t('simResultsMenu.errors.fleetUnableLoadVehicles') })}`;
      }

      if (resultsCtx && tripLinesWithPointLinks.length > 0) {
        resultsCtx.setRunSimulationDefaults({
          rideLayerId: String(resolvedLayerId),
          fleetId: importedFleetId,
        });
      }

      const capMessage = tripLinesWithPointLinks.length > TRIP_RENDER_CAP
        ? t('simResultsMenu.addToMap.capMessage', { max: TRIP_RENDER_CAP.toLocaleString() })
        : '';
      setImportFixedRoutesMessage({
        type: 'success',
        text: t('simResultsMenu.addToMap.addedOnDemandTrips', {
          total: tripLinesWithPointLinks.length.toLocaleString(),
          capMessage,
          fleetSummary,
          extraWarnings: fleetWarnings.length > 1 ? ` ${fleetWarnings.slice(1, 3).join(' ')}` : '',
        }),
      });

      setAddToMapProgressText(t('simResultsMenu.addToMap.loadingMap'));
      setIsNavigatingToMap(true);
      await yieldToPaint();
      window.requestAnimationFrame(() => navigate('/'));
    } catch (error) {
      setImportFixedRoutesMessage({
        type: 'error',
        text: error instanceof Error ? error.message : t('simResultsMenu.errors.failedAddOnDemand'),
      });
    } finally {
      setIsAddingOnDemandToMap(false);
    }
  }, [
    context,
    ridePoints,
    isAddingOnDemandToMap,
    selectedSim?.scenario_name,
    selectedSim?.run_id,
    yieldToPaint,
    navigate,
    resolveResultsIdForRun,
  ]);

  const handleImportAsFixedRouteNetwork = React.useCallback(async () => {
    if (!selectedSim?.run_id) {
      setImportFixedRoutesMessage({ type: 'error', text: t('simResultsMenu.errors.noSimulationRunSelected') });
      return;
    }
    const activities = selectedSim.activities ?? [];
    if (activities.length === 0) {
      setImportFixedRoutesMessage({ type: 'error', text: t('simResultsMenu.errors.noActivitiesForRun') });
      return;
    }

    const startedAt = Date.now();
    setIsImportingFixedRoutes(true);
    setAddToMapProgressText(t('simResultsMenu.addToMap.preparingFixedRoutes'));
    await yieldToPaint();
    try {
      trackAnalyticsEvent(ANALYTICS_EVENT_NAMES.simulation_results_import_fixed_routes_started, {
        run_id: selectedSim.run_id,
        timestamp: new Date().toISOString(),
      });

      setAddToMapProgressText(t('simResultsMenu.addToMap.loadingPathsActivities'));
      await yieldToPaint();
      const resolvedResultsId = await resolveResultsIdForRun(selectedSim.run_id);
      const roadRoutesById = await fetchRoadRoutesByResultsIdWithMetadata(resolvedResultsId, { timeoutMs: 90000 });
      setAddToMapProgressText(t('simResultsMenu.addToMap.transformingOutput'));
      await yieldToPaint();
      const routeGeometryByShift = buildShiftGeometrySeedFromRoadRoutes(roadRoutesById);
      const routeMetadataByShift = buildShiftRouteMetadataSeedFromRoadRoutes(roadRoutesById);
      const transformed = transformSimulationResultsToFixedRoutes({
        runId: selectedSim.run_id,
        activities,
        routeMetadataByShift,
        routeGeometryByShift,
      });

      if (transformed.routes.length === 0 || transformed.stops.length === 0) {
        setImportFixedRoutesMessage({
          type: 'error',
          text: t('simResultsMenu.errors.noValidFixedRoutes'),
        });
        return;
      }

      setAddToMapProgressText(t('simResultsMenu.addToMap.importingRoutesProgress', { total: transformed.routes.length.toLocaleString() }));
      await yieldToPaint();
      importSimulationResultsNetwork(transformed.stops, transformed.routes);

      window.dispatchEvent(new CustomEvent('fixedRouteImportedFromResults', {
        detail: {
          runId: selectedSim.run_id,
          layerName: 'Optimised Fixed Route Network',
          routeCount: transformed.routes.length,
          stopCount: transformed.stops.length,
        },
      }));

      const seededRouteCount = transformed.routes.filter((route) => (route.geometry?.coordinates?.length ?? 0) > 1).length;
      const missingGeometryRouteCount = transformed.routes.length - seededRouteCount;
      const mismatchWarningCount = transformed.warnings.filter((w) => w.includes('segment count mismatch')).length;
      // Count stops that resolved to a real consolidated name rather than the
      // `Stop N` fallback — surfaces whether the stop-name bridge fired.
      const namedStopCount = transformed.stops.filter((stop) => !/^Stop \d+$/.test(stop.name)).length;

      trackAnalyticsEvent(ANALYTICS_EVENT_NAMES.simulation_results_import_fixed_routes_succeeded, {
        run_id: selectedSim.run_id,
        route_count: transformed.routes.length,
        stop_count: transformed.stops.length,
        warnings_count: transformed.warnings.length,
        seeded_route_count: seededRouteCount,
        missing_geometry_route_count: missingGeometryRouteCount,
        segment_mismatch_count: mismatchWarningCount,
        named_stop_count: namedStopCount,
        duration_ms: Date.now() - startedAt,
        timestamp: new Date().toISOString(),
      });

      const summarySuffix = missingGeometryRouteCount > 0 || mismatchWarningCount > 0
        ? ` ${t('simResultsMenu.addToMap.seededRoutesSuffix', { seeded: seededRouteCount, total: transformed.routes.length })} ${missingGeometryRouteCount > 0 ? t('simResultsMenu.addToMap.missingGeometrySuffix', { count: missingGeometryRouteCount }) : ''}${mismatchWarningCount > 0 ? ` ${t('simResultsMenu.addToMap.segmentMismatchSuffix', { count: mismatchWarningCount })}` : ''}`.trim()
        : '';
      setImportFixedRoutesMessage({
        type: 'success',
        text: t('simResultsMenu.addToMap.importedFixedRoutes', {
          routes: transformed.routes.length,
          stops: transformed.stops.length,
          suffix: summarySuffix ? ` ${summarySuffix}` : '',
        }),
      });

      setAddToMapProgressText(t('simResultsMenu.addToMap.loadingMap'));
      setIsNavigatingToMap(true);
      await yieldToPaint();
      window.requestAnimationFrame(() => navigate('/'));
    } catch (error) {
      trackAnalyticsEvent(ANALYTICS_EVENT_NAMES.simulation_results_import_fixed_routes_failed, {
        run_id: selectedSim.run_id,
        duration_ms: Date.now() - startedAt,
        error_code: error instanceof Error ? error.name : 'unknown_error',
        timestamp: new Date().toISOString(),
      });
      setImportFixedRoutesMessage({
        type: 'error',
        text: error instanceof Error ? error.message : t('simResultsMenu.errors.failedImportFixedRoutes'),
      });
    } finally {
      setIsImportingFixedRoutes(false);
      setImportFixedRoutesConfirmOpen(false);
    }
  }, [selectedSim, importSimulationResultsNetwork, resolveResultsIdForRun, yieldToPaint, navigate]);

  const pageContainerSx = {
    width: '100%',
    minHeight: 'calc(100vh - 80px)',
    p: 2,
    pr: 3,
    background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
    overflow: 'auto' as const,
  };

  const activitiesToShow = React.useMemo(() => {
    const result: { position: [number, number]; activity: SimulationActivity; markerType: 'pickup' | 'dropoff' | 'depot' }[] = [];
    if (!selectedSim?.activities?.length) return result;
    const activityTimeKeys = ['activity_start_time', 'activity_start', 'min_activity_start_time', 'start_time', 'time'];
    const windowStartMs = timelineTime != null ? timelineTime.getTime() - timelineWindowMinutes * 60 * 1000 : -Infinity;
    const windowEndMs = timelineTime != null ? timelineTime.getTime() : Infinity;
    const visibleActivities = filterActivitiesByVisibility(selectedSim.activities, vehicleVisibilityMap);
    visibleActivities.forEach((activity) => {
      const timeStr = activityTimeKeys.map((k) => (activity as Record<string, unknown>)[k]).find(Boolean) as string | undefined;
      const ts = parseRequestTimeToMs(timeStr);
      if (ts != null && ts >= windowStartMs && ts <= windowEndMs) {
        const coords = getActivityCoordinates(activity);
        if (coords) {
          const sk = activityStopKindForUi(activity);
          const markerType: 'pickup' | 'dropoff' | 'depot' =
            sk === 'pickup' ? 'pickup' : sk === 'dropoff' ? 'dropoff' : 'depot';
          result.push({ position: [coords.lat, coords.lng], activity, markerType });
        }
      }
    });
    return result;
  }, [selectedSim?.activities, vehicleVisibilityMap, timelineTime, timelineWindowMinutes]);

  const visibleVehicleIds = React.useMemo(
    () => new Set(vehicleIdsForRun.filter((vehicleId) => isVehicleVisible(vehicleId, vehicleVisibilityMap))),
    [vehicleIdsForRun, vehicleVisibilityMap]
  );

  const visibleRouteLookupKeys = React.useMemo(() => {
    const keys = new Set<string>();
    visibleRidePoints.forEach((point) => {
      getRideRouteLookupKeys(point as Record<string, unknown>).forEach((k) => keys.add(k));
    });
    return keys;
  }, [visibleRidePoints]);

  const visibleRoadRoutes = React.useMemo(() => {
    const windowEndMs = timelineTime ? timelineTime.getTime() : null;
    const windowStartMs = windowEndMs != null
      ? windowEndMs - timelineWindowMinutes * 60 * 1000
      : null;

    return vehicleRoutes.filter((route) => {
      const { routeId } = route;

      // (1) Vehicle visibility: prefer metadata.vehicle_shift_id, fall back to routeId text.
      const sid = route.metadata?.vehicle_shift_id;
      let vehicleKey: string | null = null;
      if (sid) vehicleKey = shiftIdToVehicleKey.get(String(sid)) ?? String(sid);
      if (!vehicleKey) {
        vehicleKey = Array.from(visibleVehicleIds).find(
          (id) => routeId === id || routeId.startsWith(`${id}:`)
        ) ?? null;
      }
      if (vehicleKey && !isVehicleVisible(vehicleKey, vehicleVisibilityMap)) return false;

      // (2) Timeline window. Primary basis = the trip's request/pickup time (the SAME basis the
      // timeline histogram bins by and the markers filter on), so road paths appear/disappear with
      // the timeline for EVERY route, not only those carrying full segment metadata. A route is
      // visible when any onboard trip's request/pickup time falls in the window.
      // Deep fallback (trip-less/deadhead legs with no resolvable trip time): the segment "live"
      // window between the from-activity end (departure) and to-activity start (arrival).
      if (windowStartMs != null && windowEndMs != null) {
        const tripTimesMs = roadRouteRequestTimesMs(route, ridePointByIdMap);
        if (tripTimesMs.length > 0) {
          if (!tripTimesMs.some((ms) => ms >= windowStartMs && ms <= windowEndMs)) return false;
        } else {
          const fromN = route.metadata?.from_driver_activity_number;
          const toN = route.metadata?.to_driver_activity_number;
          if (sid && fromN != null && toN != null) {
            const inner = shiftActivityTimeMs.get(String(sid));
            const fromAct = inner?.get(fromN);
            const toAct = inner?.get(toN);
            if (fromAct && toAct) {
              const routeStart = Math.min(fromAct.endMs, toAct.startMs);
              const routeEnd = Math.max(fromAct.endMs, toAct.startMs);
              if (routeEnd < windowStartMs || routeStart > windowEndMs) return false;
            }
          }
        }
      }

      // (3) Lookup-key filter: keeps routes that have no recognised vehicle key but match a
      //     visible ride point's lookup keys. Routes with a recognised vehicle key already
      //     passed (1) and don't need to clear this gate.
      if (!vehicleKey && visibleRouteLookupKeys.size > 0) {
        const lower = routeId.toLowerCase();
        const strong = Array.from(visibleRouteLookupKeys).filter((key) => key.trim().length >= 6);
        const matched =
          Array.from(visibleRouteLookupKeys).some((key) => routeId === key) ||
          strong.some((key) => lower.includes(key.toLowerCase()));
        if (!matched) return false;
      }

      return true;
    });
  }, [
    vehicleRoutes,
    vehicleVisibilityMap,
    visibleVehicleIds,
    visibleRouteLookupKeys,
    shiftIdToVehicleKey,
    shiftActivityTimeMs,
    ridePointByIdMap,
    timelineTime,
    timelineWindowMinutes,
  ]);

  // Not windowed by the timeline: the playback layer cuts trails by time itself.
  const playbackSegments = React.useMemo(
    () =>
      vehicleRoutes
        .filter((route) => {
          const sid = route.metadata?.vehicle_shift_id;
          return sid != null && isVehicleVisible(shiftIdToVehicleKey.get(String(sid)) ?? String(sid), vehicleVisibilityMap);
        })
        .map((route) => ({
          coordinates: route.geometry.coordinates,
          vehicleShiftId: route.metadata?.vehicle_shift_id,
          fromActivityNumber: route.metadata?.from_driver_activity_number,
          toActivityNumber: route.metadata?.to_driver_activity_number,
          // /paths lists each rider under several ids (ride UUID and external reference).
          onboardCount: new Set(
            (route.metadata?.rides_onboard_ids ?? []).map((id) => ridePointByIdMap.get(id) ?? id)
          ).size,
        })),
    [vehicleRoutes, shiftIdToVehicleKey, vehicleVisibilityMap, ridePointByIdMap]
  );

  const tripPanelGoogleMapsUrl = React.useMemo(() => {
    const d = mapTripInfoPanelData;
    if (!d) return null;
    const rt = d.requestTime.trim();
    const missing = rt === '' || rt === EM_DASH || rt === 'â€”';
    return buildUnmatchedRideGoogleMapsDrivingUrl({
      from_latitude: d.mapsFromLat,
      from_longitude: d.mapsFromLng,
      to_latitude: d.mapsToLat,
      to_longitude: d.mapsToLng,
      from_address: d.mapsFromAddress,
      to_address: d.mapsToAddress,
      request_time: missing ? undefined : d.requestTime,
    });
  }, [mapTripInfoPanelData]);

  const tripPanelFieldBundle = React.useMemo(() => {
    const d = mapTripInfoPanelData;
    if (!d) return null;
    const fromAddress = typeof d.mapsFromAddress === 'string' ? d.mapsFromAddress.trim() : '';
    const toAddress = typeof d.mapsToAddress === 'string' ? d.mapsToAddress.trim() : '';
    const fromUsesAddress = fromAddress.length > 0;
    const toUsesAddress = toAddress.length > 0;
    const fromCoordLine = formatCoordPair(d.mapsFromLat, d.mapsFromLng);
    const toCoordLine = formatCoordPair(d.mapsToLat, d.mapsToLng);
    const fromLine = fromUsesAddress ? fromAddress : fromCoordLine;
    const toLine = toUsesAddress ? toAddress : toCoordLine;
    return {
      d,
      fromCopy: latLonClipboardText(d.mapsFromLat, d.mapsFromLng),
      toCopy: latLonClipboardText(d.mapsToLat, d.mapsToLng),
      fromLine,
      toLine,
      fromCoordLine,
      toCoordLine,
      fromUsesAddress,
      toUsesAddress,
      pickupTimeOnly: formatTripPanelTimeOnlyUtc(d.pickupStopTime),
      dropoffTimeOnly: formatTripPanelTimeOnlyUtc(d.dropoffStopTime),
    };
  }, [mapTripInfoPanelData]);

  const tripPanelMergedDetailRows = React.useMemo(
    () => (mapTripInfoPanelData ? mergeTripPanelDetailRows(mapTripInfoPanelData, t) : []),
    [mapTripInfoPanelData, t]
  );

  const focusedRoadRoutes = React.useMemo(
    () =>
      highlightedTripMatchKeys.length > 0
        ? visibleRoadRoutes.filter((route) =>
            vehicleRouteMatchesTrip(
              route,
              highlightedTripMatchKeys,
              highlightedTripRidePoint,
              highlightedTripShiftIds,
              selectedSim?.activities
            )
          )
        : [],
    [highlightedTripMatchKeys, visibleRoadRoutes, highlightedTripRidePoint, highlightedTripShiftIds, selectedSim?.activities]
  );

  const focusedRoadGradientPathSafe = React.useMemo((): [number, number][] => {
    if (focusedRoadRoutes.length === 0) return [];
    const path = mergeRoadRoutesForTripGradient(focusedRoadRoutes, highlightedTripRidePoint);
    if (path.length >= 2) return path;
    return focusedRoadRoutes[0]?.geometry.coordinates.map(([lng, lat]) => [lat, lng] as [number, number]) ?? [];
  }, [focusedRoadRoutes, highlightedTripRidePoint]);

  const focusedRoadGradientSegmentCount = React.useMemo(
    () => Math.max(1, focusedRoadGradientPathSafe.length - 1),
    [focusedRoadGradientPathSafe]
  );

  // Map-first layout for Simulation Results tab (full-page variant)
  if (isPage) {
    const points = ridePoints ?? [];
    const pointsToShow = visibleRidePoints;
    const visiblePointsToShow = filterRidePointsByVisibilityFast(pointsToShow, vehicleVisibilityMap, sortedVehicleIds);
    const mapCenter = visiblePointsToShow.length > 0
      ? [visiblePointsToShow[0].from_latitude, visiblePointsToShow[0].from_longitude] as [number, number]
      : points.length > 0
        ? [points[0].from_latitude, points[0].from_longitude] as [number, number]
        : [-33.87, 151.21] as [number, number];
    const mapZoom = points.length > 0 ? 12 : 10;
    // Road paths whenever /paths returned routes; O–D lines while loading or when it has none.
    const showRoadPolylines = vehicleRoutes.length > 0;
    const tripFocusActive = highlightedTripMatchKeys.length > 0;
    const mapTripFocusActive =
      tripFocusActive || (unallocatedVisible && highlightedUnmatchedRide != null);
    const unmatchedRidesMap = unallocatedVisible
      ? (selectedSim?.unmatched_rides ?? []).filter(unmatchedRideHasMapCoords)
      : [];
    const highlightedTripEndpoints: { pickup?: [number, number]; dropoff?: [number, number] } = {};
    if (highlightedTripRidePoint) {
      const fromLat = highlightedTripRidePoint.from_latitude;
      const fromLng = highlightedTripRidePoint.from_longitude;
      const toLat = highlightedTripRidePoint.to_latitude;
      const toLng = highlightedTripRidePoint.to_longitude;
      if (typeof fromLat === 'number' && typeof fromLng === 'number') {
        highlightedTripEndpoints.pickup = [fromLat, fromLng];
      }
      if (typeof toLat === 'number' && typeof toLng === 'number') {
        highlightedTripEndpoints.dropoff = [toLat, toLng];
      }
    }
    return (
      <Box
        sx={{
          position: 'fixed',
          top: 72,
          left: 0,
          right: 0,
          bottom: 0,
          overflow: 'hidden',
          backgroundColor: '#FFFFFF',
          '--sim-map-zoom-bottom-offset': `${deckTotalHeight}px`,
        }}
      >
        {/* Full-screen map - direct child of page wrapper, covers viewport */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            width: '100%',
            height: '100%',
            zIndex: 0,
          }}
        >
          <ResultsMapLayers
            mapCenter={mapCenter}
            mapZoom={mapZoom}
            points={points}
            fitKey={selectedSim?.run_id ?? 'default'}
            visiblePointsToShow={visiblePointsToShow}
            activitiesToShow={activitiesToShow}
            unmatchedRidesMap={unmatchedRidesMap}
            visibleRoadRoutes={visibleRoadRoutes}
            focusedRoadRoutes={focusedRoadRoutes}
            focusedRoadGradientPathSafe={focusedRoadGradientPathSafe}
            focusedRoadGradientSegmentCount={focusedRoadGradientSegmentCount}
            showRoadPolylines={showRoadPolylines}
            playback={
              timelineTime
                ? { segments: playbackSegments, activityTimes: shiftActivityTimeMs, timeMs: timelineTime.getTime() }
                : null
            }
            tripFocusActive={tripFocusActive}
            mapTripFocusActive={mapTripFocusActive}
            highlightedTripMatchKeys={highlightedTripMatchKeys}
            highlightedTripMatchKeySet={highlightedTripMatchKeySet}
            highlightedTripRidePoint={highlightedTripRidePoint}
            highlightedTripShiftIds={highlightedTripShiftIds}
            highlightedTripEndpoints={highlightedTripEndpoints}
            matchingActivitySet={matchingActivitySet}
            matchingRidePointSet={matchingRidePointSet}
            activities={selectedSim?.activities ?? []}
            highlightedActivity={highlightedActivity}
            highlightedUnmatchedRide={highlightedUnmatchedRide}
            onClearTripFocus={handleClearTripFocus}
            onSelectActivityMarker={handleSelectActivityMarker}
            onSelectRidePoint={handleSelectRidePoint}
            onSelectUnmatchedRide={handleSelectUnmatchedRide}
            ridesError={ridesError}
          />

          {loadingDetailedData && selectedRunId && (
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                zIndex: 900,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: 'rgba(255, 255, 255, 0.58)',
                backdropFilter: 'blur(12px)',
                WebkitBackdropFilter: 'blur(12px)',
                animation: `${simMapLoaderFadeIn} 0.28s ease-out`,
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 2.25,
                  px: 4,
                  py: 3.5,
                  maxWidth: 280,
                  borderRadius: 3,
                  backgroundColor: 'rgba(255, 255, 255, 0.96)',
                  boxShadow:
                    '0 28px 56px rgba(58, 58, 72, 0.14), 0 0 0 1px rgba(238, 115, 52, 0.14)',
                }}
              >
                <Box
                  sx={{
                    position: 'relative',
                    width: 88,
                    height: 88,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <CircularProgress
                    size={84}
                    thickness={1.2}
                    sx={{
                      position: 'absolute',
                      color: 'rgba(238, 115, 52, 0.22)',
                      animationDuration: '2.4s',
                    }}
                  />
                  <CircularProgress
                    size={52}
                    thickness={4}
                    sx={{
                      color: '#EE7334',
                      filter: 'drop-shadow(0 2px 8px rgba(238, 115, 52, 0.35))',
                    }}
                  />
                  <Box
                    sx={{
                      position: 'absolute',
                      left: '50%',
                      top: '50%',
                      transform: 'translate(-50%, -50%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      pointerEvents: 'none',
                    }}
                  >
                    <Icon size={20} name="route" style={{
                        fontSize: 22,
                        color: '#EE7334',
                        opacity: 0.95,
                      }} />
                  </Box>
                </Box>
                <Box sx={{ textAlign: 'left' }}>
                  <Typography
                    sx={{
                      fontFamily: '"Montserrat", "Roboto", sans-serif',
                      fontWeight: 700,
                      fontSize: '1rem',
                      color: '#3A3A48',
                      letterSpacing: '0.03em',
                    }}
                  >
                    {t('simResultsMenu.loadingOverlay.loadingResults')}
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: '"Roboto", sans-serif',
                      fontWeight: 500,
                      fontSize: '0.8125rem',
                      color: 'rgba(58, 58, 72, 0.62)',
                      mt: 0.75,
                      lineHeight: 1.45,
                    }}
                  >
                    {t('simResultsMenu.loadingOverlay.updatingMap')}
                  </Typography>
                </Box>
              </Box>
            </Box>
          )}

          {isGeneratingRoadRoutes && (
            <Box
              sx={{
                position: 'absolute',
                top: 16,
                left: '50%',
                transform: 'translateX(-50%)',
                zIndex: 1300,
                backgroundColor: 'rgba(255, 255, 255, 0.95)',
                px: 2,
                py: 1,
                borderRadius: 2,
                boxShadow: '0 2px 10px rgba(0,0,0,0.18)',
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}
            >
              <CircularProgress size={18} />
              <Typography sx={{ fontSize: '0.875rem', fontWeight: 500 }}>
                {t('simResultsMenu.mapStatus.generatingRoadRoutes')}
              </Typography>
            </Box>
          )}

          {routeGenerationSummary && !isGeneratingRoadRoutes && (
            <Box
              sx={{
                position: 'absolute',
                top: 16,
                right: 16,
                zIndex: 1200,
                backgroundColor: 'rgba(255, 255, 255, 0.95)',
                px: 1.5,
                py: 0.75,
                borderRadius: 1.5,
                boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
              }}
            >
              <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                {t('simResultsMenu.mapStatus.roadRoutesSummary', { count: routeGenerationSummary.succeeded })}
              </Typography>
            </Box>
          )}

          {tripPanelFieldBundle && (
            <Box
              sx={{
                position: 'absolute',
                top: 12,
                right: 388,
                left: 'auto',
                transform: 'none',
                zIndex: 1210,
                pointerEvents: 'auto',
                width: 'fit-content',
                maxWidth: 'min(1040px, max(520px, calc(100vw - 440px)))',
                px: 0.9,
                py: 0.55,
                borderRadius: 2.25,
                border: '1px solid rgba(255,255,255,0.12)',
                background:
                  'linear-gradient(180deg, rgba(20,22,31,0.96) 0%, rgba(16,18,28,0.96) 100%)',
                color: '#F7F8FC',
                boxShadow:
                  '0 14px 34px rgba(0,0,0,0.34), inset 0 1px 0 rgba(255,255,255,0.06)',
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'stretch',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 0,
                  pr: 0.8,
                  mr: 0.8,
                  borderRight: '1px solid rgba(255,255,255,0.1)',
                  flexShrink: 0,
                  width: 32,
                }}
              >
                <Icon size={18} d={ICON_FALLBACKS.Timeline} style={{ color: '#9DCBFF' }} />
              </Box>

              <Box
                sx={{
                  flex: '0 1 auto',
                  minWidth: 0,
                  maxWidth: '100%',
                  display: 'grid',
                  gridTemplateColumns: 'max-content max-content',
                  alignItems: 'center',
                  columnGap: 0.9,
                  py: 0,
                }}
              >
                <Box
                  sx={{
                    flex: '0 0 auto',
                    minWidth: 'max-content',
                    maxWidth: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    gap: 0,
                    pr: 0.8,
                    mr: 0.8,
                    pb: 0,
                    borderRight: '1px solid rgba(255,255,255,0.1)',
                    borderBottom: 'none',
                  }}
                >
                  <Box
                    sx={{
                      width: '100%',
                      display: 'grid',
                      gridTemplateColumns: '52px max-content',
                      columnGap: 0.55,
                      alignItems: 'center',
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 0.05,
                      }}
                    >
                      <Typography component="span" sx={{ ...tripPanelStatLabelSx, mb: 0 }}>
                        {t('simResultsMenu.tripPanel.from')}
                      </Typography>
                      <Icon size={15} d={ICON_FALLBACKS.ArrowUpward} style={{ color: '#7CE2B4', opacity: 0.92 }} />
                    </Box>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.12 }}>
                      <Box
                        sx={{
                          display: 'inline-flex',
                          flexDirection: 'row',
                          alignItems: 'center',
                          justifyContent: 'flex-start',
                          gap: 0.35,
                          flexWrap: 'nowrap',
                        }}
                      >
                        <Typography
                          component="span"
                          title={tripPanelFieldBundle.fromUsesAddress ? tripPanelFieldBundle.fromLine : undefined}
                          sx={{
                            fontSize: '0.74rem',
                            fontWeight: 700,
                            color: '#F7F9FF',
                            fontFamily: tripPanelFieldBundle.fromUsesAddress
                              ? '"Roboto", sans-serif'
                              : 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
                            whiteSpace: 'nowrap',
                            maxWidth: '13.5rem',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                        >
                          {tripPanelFieldBundle.fromLine}
                        </Typography>
                        {tripPanelFieldBundle.fromCopy ? (
                          <Box sx={{ display: 'flex', flexShrink: 0, alignItems: 'center' }}>
                            <QuickCopyButton
                              textToCopy={tripPanelFieldBundle.fromCopy}
                              ariaLabel={t('simResultsMenu.tripPanel.copyFromCoordsAria')}
                              tone="onDark"
                            />
                          </Box>
                        ) : null}
                      </Box>
                      {tripPanelFieldBundle.fromUsesAddress ? (
                        <Typography
                          component="span"
                          sx={{
                            fontSize: '0.68rem',
                            fontWeight: 600,
                            color: 'rgba(247,249,255,0.78)',
                            fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
                            whiteSpace: 'nowrap',
                            maxWidth: '13.5rem',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                          title={tripPanelFieldBundle.fromCoordLine}
                        >
                          {tripPanelFieldBundle.fromCoordLine}
                        </Typography>
                      ) : null}
                      <SimulationResultsTooltip
                        title={t('simResultsMenu.tripPanel.pickupUtcTooltip', { time: tripPanelFieldBundle.d.pickupStopTime })}
                        placement="bottom"
                      >
                        <Typography
                          component="span"
                          sx={{
                            fontSize: '0.7rem',
                            fontWeight: 600,
                            color: 'rgba(247,249,255,0.78)',
                            fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
                            fontVariantNumeric: 'tabular-nums',
                            letterSpacing: '0.02em',
                            whiteSpace: 'nowrap',
                            textAlign: 'left',
                          }}
                        >
                          {tripPanelFieldBundle.pickupTimeOnly}
                          {tripPanelFieldBundle.pickupTimeOnly !== EM_DASH ? (
                            <Typography component="span" sx={{ fontSize: '0.62rem', opacity: 0.55, ml: 0.35 }}>
                              UTC
                            </Typography>
                          ) : null}
                        </Typography>
                      </SimulationResultsTooltip>
                    </Box>
                  </Box>

                  <Box
                    sx={{
                      width: '100%',
                      borderTop: '1px solid rgba(255,255,255,0.1)',
                      pt: 0.45,
                      mt: 0.45,
                    }}
                  >
                    <Box
                      sx={{
                        width: '100%',
                        display: 'grid',
                        gridTemplateColumns: '52px max-content',
                        columnGap: 0.55,
                        alignItems: 'center',
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: 0.05,
                        }}
                      >
                        <Typography component="span" sx={{ ...tripPanelStatLabelSx, mb: 0 }}>
                          {t('simResultsMenu.tripPanel.to')}
                        </Typography>
                        <Icon size={15} name="arrow-down-long" style={{ color: '#FFB38A', opacity: 0.92 }} />
                      </Box>
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.12 }}>
                        <Box
                          sx={{
                            display: 'inline-flex',
                            flexDirection: 'row',
                            alignItems: 'center',
                            justifyContent: 'flex-start',
                            gap: 0.35,
                            flexWrap: 'nowrap',
                          }}
                        >
                          <Typography
                            component="span"
                            title={tripPanelFieldBundle.toUsesAddress ? tripPanelFieldBundle.toLine : undefined}
                            sx={{
                              fontSize: '0.74rem',
                              fontWeight: 700,
                              color: '#F7F9FF',
                              fontFamily: tripPanelFieldBundle.toUsesAddress
                                ? '"Roboto", sans-serif'
                                : 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
                              whiteSpace: 'nowrap',
                              maxWidth: '13.5rem',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                            }}
                          >
                            {tripPanelFieldBundle.toLine}
                          </Typography>
                          {tripPanelFieldBundle.toCopy ? (
                            <Box sx={{ display: 'flex', flexShrink: 0, alignItems: 'center' }}>
                              <QuickCopyButton
                                textToCopy={tripPanelFieldBundle.toCopy}
                                ariaLabel={t('simResultsMenu.tripPanel.copyToCoordsAria')}
                                tone="onDark"
                              />
                            </Box>
                          ) : null}
                        </Box>
                        {tripPanelFieldBundle.toUsesAddress ? (
                          <Typography
                            component="span"
                            sx={{
                              fontSize: '0.68rem',
                              fontWeight: 600,
                              color: 'rgba(247,249,255,0.78)',
                              fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
                              whiteSpace: 'nowrap',
                              maxWidth: '13.5rem',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                            }}
                            title={tripPanelFieldBundle.toCoordLine}
                          >
                            {tripPanelFieldBundle.toCoordLine}
                          </Typography>
                        ) : null}
                        <SimulationResultsTooltip
                          title={t('simResultsMenu.tripPanel.dropoffUtcTooltip', { time: tripPanelFieldBundle.d.dropoffStopTime })}
                          placement="bottom"
                        >
                          <Typography
                            component="span"
                            sx={{
                              fontSize: '0.7rem',
                              fontWeight: 600,
                              color: 'rgba(247,249,255,0.78)',
                              fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
                              fontVariantNumeric: 'tabular-nums',
                              letterSpacing: '0.02em',
                              whiteSpace: 'nowrap',
                              textAlign: 'left',
                            }}
                          >
                            {tripPanelFieldBundle.dropoffTimeOnly}
                            {tripPanelFieldBundle.dropoffTimeOnly !== EM_DASH ? (
                              <Typography component="span" sx={{ fontSize: '0.62rem', opacity: 0.55, ml: 0.35 }}>
                                UTC
                              </Typography>
                            ) : null}
                          </Typography>
                        </SimulationResultsTooltip>
                      </Box>
                    </Box>
                  </Box>
                </Box>

                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    gap: 0.35,
                    justifyContent: 'center',
                    flex: '0 0 auto',
                    minWidth: '9.2rem',
                    width: 'auto',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', gap: 0.95 }}>
                    <Box sx={{ minWidth: '3.3rem' }}>
                      <TripPanelStat label={t('simResultsMenu.tripPanel.pax')} align="center" icon={<Icon size={15} name="users" style={{ color: '#D2BBFF' }} />}>
                        <Typography sx={{ fontSize: '0.76rem', fontWeight: 600, color: '#F7F9FF', textAlign: 'center', whiteSpace: 'nowrap' }}>
                          {tripPanelFieldBundle.d.passengers}
                        </Typography>
                      </TripPanelStat>
                    </Box>
                    <Box sx={{ minWidth: '4.8rem' }}>
                      <TripPanelStat label={t('simResultsMenu.tripPanel.travel')} align="center" icon={<Icon size={15} d={ICON_FALLBACKS.Timer} style={{ color: '#FFE08A' }} />}>
                        <SimulationResultsTooltip title={tripPanelFieldBundle.d.travelTimeTooltip} placement="bottom">
                          <Typography
                            sx={{
                              fontSize: '0.76rem',
                              fontWeight: 600,
                              color: '#F7F9FF',
                              textAlign: 'center',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {tripPanelFieldBundle.d.totalTravelTime}
                          </Typography>
                        </SimulationResultsTooltip>
                      </TripPanelStat>
                    </Box>
                  </Box>
                  <Box
                    sx={{
                      width: '100%',
                      borderTop: '1px solid rgba(255,255,255,0.1)',
                      pt: 0.45,
                      mt: 0.45,
                    }}
                  >
                    <TripPanelStat label={t('simResultsMenu.tripPanel.vehicle')} align="center" icon={<Icon size={15} name="car-side" style={{ color: '#9DCBFF' }} />}>
                      <Typography sx={{ fontSize: '0.76rem', fontWeight: 600, color: '#F7F9FF', textAlign: 'center' }}>
                        {tripPanelFieldBundle.d.pickupVehicle}
                      </Typography>
                    </TripPanelStat>
                  </Box>
                </Box>
              </Box>

              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 0.25,
                  flexShrink: 0,
                  pl: 0.7,
                  ml: 0.2,
                  borderLeft: '1px solid rgba(255,255,255,0.1)',
                  alignSelf: 'stretch',
                }}
              >
                <Tooltip
                  enterDelay={200}
                  title={
                    tripPanelGoogleMapsUrl
                      ? t('simResultsMenu.tripPanel.directionsOpenTooltip', {
                          time: formatTripPanelRequestTimeUtcHm(tripPanelFieldBundle.d.requestTime),
                        })
                      : t('simResultsMenu.tripPanel.directionsUnavailableTooltip')
                  }
                >
                  <span>
                    <IconButton
                      size="small"
                      disabled={!tripPanelGoogleMapsUrl}
                      aria-label={
                        tripPanelGoogleMapsUrl
                          ? t('simResultsMenu.tripPanel.directionsAria')
                          : t('simResultsMenu.tripPanel.directionsUnavailableAria')
                      }
                      onClick={(e) => {
                        e.stopPropagation();
                        if (!tripPanelGoogleMapsUrl) return;
                        window.open(tripPanelGoogleMapsUrl, '_blank', 'noopener,noreferrer');
                      }}
                      sx={{ ...tripPanelToolbarIconButtonSx, ...tripPanelToolbarIconButtonDisabledSx }}
                    >
                      <Icon size={16} name="car-side" />
                    </IconButton>
                  </span>
                </Tooltip>
                <TripPanelIdentifiersIcon rows={tripPanelMergedDetailRows} />
                <IconButton
                  size="small"
                  onClick={() => setMapTripInfoPanelData(null)}
                  sx={{ ...tripPanelToolbarIconButtonSx }}
                >
                  <Icon size={14} name="xmark" />
                </IconButton>
              </Box>
            </Box>
          )}

          {mapTripFocusActive && (
            <Box
              sx={{
                position: 'absolute',
                top: 16,
                left: 16,
                zIndex: 1200,
              }}
            >
              <Chip
                label={
                  tripFocusActive
                    ? t('simResultsMenu.tripPanel.tripChip', {
                        id: `${(highlightedTripRideId ?? highlightedTripMatchKeys[0] ?? '').slice(0, 18)}${
                          (highlightedTripRideId ?? highlightedTripMatchKeys[0] ?? '').length > 18 ? 'â€¦' : ''
                        }`,
                      })
                    : (() => {
                        const r = highlightedUnmatchedRide;
                        if (!r) return t('simResultsMenu.tripPanel.unallocatedChipFallback');
                        const raw =
                          normalizeTripId(r.ride_id) ??
                          normalizeTripId(r.request_id) ??
                          normalizeTripId(r.external_reference_id) ??
                          unmatchedRideStableKey(r).replace(/^um:/, '');
                        const s = raw.slice(0, 18);
                        const ell = raw.length > 18 ? 'â€¦' : '';
                        return t('simResultsMenu.tripPanel.unallocatedChip', { id: `${s}${ell}` });
                      })()
                }
                onDelete={() => {
                  setHighlightedTripRideId(null);
                  setHighlightedTripMatchKeys([]);
                  setHighlightedActivity(null);
                  setHighlightedUnmatchedRide(null);
                  setMapTripInfoPanelData(null);
                }}
                color="warning"
                variant="outlined"
                size="small"
                sx={{ backgroundColor: 'rgba(255,255,255,0.96)', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}
              />
            </Box>
          )}
        </Box>

        {/* Left sidebar: Run selector + Key Metrics */}
        <SimulationResultsSidebar
          selectedRunId={selectedRunId}
          onSelectedRunIdChange={setSelectedRunId}
          simulationResults={simulationResults}
          loadingResults={loadingResults}
          resultsError={resultsError}
          onReload={handleReload}
          selectedSim={selectedSim}
          metrics={SIDEBAR_METRICS}
          onBackToMap={handleBackToMap}
          onShare={selectedSim ? () => {
            setShareResultsId(selectedSim.results_id ?? selectedSim.run_id ?? null);
            setShareResultsTitle(selectedSim.scenario_name ? t('simResultsMenu.shareDialog.titleWithName', { name: selectedSim.scenario_name }) : t('simResultsMenu.shareDialog.fallbackTitle'));
            setShareRole(selectedSim.role);
          } : undefined}
          deckTotalHeight={deckTotalHeight}
          showInsights={!isPage}
          onAddToMapOnDemand={handleAddToMap}
          onAddToMapFixedRoute={() => setImportFixedRoutesConfirmOpen(true)}
          addToMapOnDemandDisabled={!selectedSim || (!ridePoints || ridePoints.length === 0)}
          addToMapFixedRouteDisabled={!selectedSim || !(selectedSim.activities && selectedSim.activities.length > 0)}
          addToMapBusy={isImportingFixedRoutes || isAddingOnDemandToMap}
        />

        {/* Multi-view Analytics Deck: Timeline, Travel Dist., Unmatched, Operations. */}
        <AnalyticsDeck
          ridePoints={points}
          currentTime={timelineTime}
          onTimeChange={setTimelineTime}
          windowMinutes={timelineWindowMinutes}
          onWindowMinutesChange={setTimelineWindowMinutes}
          unmatchedRides={selectedSim?.unmatched_rides ?? undefined}
          activities={selectedSim?.activities ?? undefined}
          runId={selectedSim?.run_id ?? undefined}
          statsSummary={selectedSim}
          onHighlightActivity={handleHighlightActivity}
          onVehicleSelectForDetailPanel={setSelectedVehicleForDetailPanel}
          vehicleVisibilityMap={vehicleVisibilityMap}
          onVehicleVisibilityChange={handleVehicleVisibilityChange}
          onShowAllVehicles={handleShowAllVehicles}
          onHideAllVehicles={handleHideAllVehicles}
          selectedUnmatchedRideKey={
            highlightedUnmatchedRide ? unmatchedRideStableKey(highlightedUnmatchedRide) : null
          }
          onSelectUnmatchedRide={handleSelectUnmatchedRide}
          unallocatedVisible={unallocatedVisible}
          onUnallocatedVisibleChange={handleUnallocatedVisibleChange}
          onDeckHeightChange={setDeckTotalHeight}
          selectedSim={selectedSim}
          simulationOptions={simulationOptionsForSelect}
        />

        {/* Right-side vehicle activity detail panel: fits between header and bottom Operations/Timeline deck */}
        {selectedVehicleForDetailPanel && (
          <Box
            sx={{
              position: 'fixed',
              top: 80,
              right: 0,
              bottom: 290,
              width: 360,
              maxWidth: '100%',
              zIndex: 1200,
              boxShadow: '-4px 0 20px rgba(0,0,0,0.15)',
              overflow: 'hidden',
            }}
          >
            <DriverActivityDetailPanel
              vehicle={selectedVehicleForDetailPanel}
              onClose={() => {
                setSelectedVehicleForDetailPanel(null);
                setHighlightedActivity(null);
                setHighlightedTripRideId(null);
                setHighlightedTripMatchKeys([]);
                setHighlightedUnmatchedRide(null);
                setMapTripInfoPanelData(null);
              }}
              runId={selectedSim?.run_id ?? undefined}
              onActivitySelect={handleHighlightActivity}
            />
          </Box>
        )}

        {(isNavigatingToMap || isAddingOnDemandToMap || isImportingFixedRoutes) && (
          <Box
            sx={{
              position: 'fixed',
              inset: 0,
              zIndex: 4000,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: 'rgba(255, 255, 255, 0.58)',
              backdropFilter: 'blur(12px)',
              WebkitBackdropFilter: 'blur(12px)',
              animation: `${simMapLoaderFadeIn} 0.28s ease-out`,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 2.25,
                px: 4,
                py: 3.5,
                maxWidth: 280,
                borderRadius: 3,
                backgroundColor: 'rgba(255, 255, 255, 0.96)',
                boxShadow:
                  '0 28px 56px rgba(58, 58, 72, 0.14), 0 0 0 1px rgba(238, 115, 52, 0.14)',
              }}
            >
              <Box
                sx={{
                  position: 'relative',
                  width: 88,
                  height: 88,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <CircularProgress
                  size={84}
                  thickness={1.2}
                  sx={{
                    position: 'absolute',
                    color: 'rgba(238, 115, 52, 0.22)',
                    animationDuration: '2.4s',
                  }}
                />
                <CircularProgress
                  size={52}
                  thickness={4}
                  sx={{
                    color: '#EE7334',
                    filter: 'drop-shadow(0 2px 8px rgba(238, 115, 52, 0.35))',
                  }}
                />
                <Box
                  sx={{
                    position: 'absolute',
                    left: '50%',
                    top: '50%',
                    transform: 'translate(-50%, -50%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    pointerEvents: 'none',
                  }}
                >
                  <Icon size={20} name="route" style={{
                      fontSize: 22,
                      color: '#EE7334',
                      opacity: 0.95,
                    }} />
                </Box>
              </Box>
              <Box sx={{ textAlign: 'left' }}>
                <Typography
                  sx={{
                    fontFamily: '"Montserrat", "Roboto", sans-serif',
                    fontWeight: 700,
                    fontSize: '1rem',
                    color: '#3A3A48',
                    letterSpacing: '0.03em',
                  }}
                >
                  {isAddingOnDemandToMap ? t('simResultsMenu.loadingOverlay.addingToMap') : isImportingFixedRoutes ? t('simResultsMenu.loadingOverlay.importingFixedRoutes') : t('simResultsMenu.loadingOverlay.loadingMap')}
                </Typography>
                <Typography
                  sx={{
                    fontFamily: '"Roboto", sans-serif',
                    fontWeight: 500,
                    fontSize: '0.8125rem',
                    color: 'rgba(58, 58, 72, 0.62)',
                    mt: 0.75,
                    lineHeight: 1.45,
                  }}
                >
                  {addToMapProgressText ?? t('simResultsMenu.loadingOverlay.preparingLayers')}
                </Typography>
              </Box>
            </Box>
          </Box>
        )}

        <Snackbar
          open={routeFailureWarning != null}
          autoHideDuration={6000}
          onClose={() => setRouteFailureWarning(null)}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert onClose={() => setRouteFailureWarning(null)} severity="warning" variant="filled" sx={{ width: '100%' }}>
            {routeFailureWarning}
          </Alert>
        </Snackbar>
        <Snackbar
          open={importFixedRoutesMessage != null}
          autoHideDuration={7000}
          onClose={() => setImportFixedRoutesMessage(null)}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
          sx={{ top: { xs: 132, sm: 150 } }}
        >
          <Alert
            onClose={() => setImportFixedRoutesMessage(null)}
            severity={importFixedRoutesMessage?.type === 'success' ? 'success' : 'error'}
            variant="filled"
            sx={{ width: '100%' }}
          >
            {importFixedRoutesMessage?.text}
          </Alert>
        </Snackbar>
        <Dialog
          open={importFixedRoutesConfirmOpen}
          onClose={() => (isImportingFixedRoutes ? undefined : setImportFixedRoutesConfirmOpen(false))}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>{t('simResultsMenu.importDialog.title')}</DialogTitle>
          <DialogContent>
            <DialogContentText>
              {t('simResultsMenu.importDialog.body')}
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setImportFixedRoutesConfirmOpen(false)} disabled={isImportingFixedRoutes}>
              {t('simResultsMenu.importDialog.cancel')}
            </Button>
            <Button onClick={handleImportAsFixedRouteNetwork} variant="contained" disabled={isImportingFixedRoutes}>
              {isImportingFixedRoutes ? t('simResultsMenu.importDialog.importing') : t('simResultsMenu.importDialog.import')}
            </Button>
          </DialogActions>
        </Dialog>
        <ShareAccessDialog
          open={Boolean(shareResultsId)}
          title={shareResultsTitle || t('simResultsMenu.shareDialog.fallbackTitle')}
          currentUserEmail={currentUserEmail}
          canManage={shareRole === 'Owner'}
          listShares={() => listSimulationResultsShares(shareResultsId!)}
          share={(email, role) => shareSimulationResults(shareResultsId!, { email, role })}
          revoke={(email) => revokeSimulationResultsShare(shareResultsId!, email)}
          onClose={() => setShareResultsId(null)}
        />
      </Box>
    );
  }

  const drawerPaperSx = (theme: any) => ({
    width: 'calc(65vw - 65px)',
    maxWidth: 960,
    p: 2,
    pr: 2,
    height: 'calc(100vh - 80px)',
    top: 80,
    right: 65,
    overflow: 'hidden' as const,
    backgroundColor: opsColors.neutral.panel,
    border: `1px solid ${opsColors.neutral.line}`,
    backdropFilter: 'none',
    WebkitBackdropFilter: 'none',
    boxShadow: opsElevation.drawer.shadow,
    borderTopLeftRadius: 16,
    borderBottomLeftRadius: 16,
    borderTopRightRadius: 0,
    borderBottomRightRadius: 0,
    zIndex: 2201,
  });

  const innerContent = (
      <Box
        sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
        data-testid={isPage ? 'sim-results.page.root' : 'sim-results.drawer.root'}
        data-loading={loadingResults || loadingDetailedData ? 'true' : 'false'}
        data-error={resultsError || detailedDataError || manualError ? 'true' : 'false'}
        data-empty={simulationHistory.length === 0 ? 'true' : 'false'}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {t('simResultsMenu.title')}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {(selectedSim?.results_id || selectedSim?.run_id) && (
              <Tooltip title={t('simResultsMenu.header.manageAccess')}>
                <span>
                  <IconButton
                    size="small"
                    onClick={() => {
                      setShareResultsId(selectedSim!.results_id ?? selectedSim!.run_id ?? null);
                      setShareResultsTitle(selectedSim!.scenario_name ? t('simResultsMenu.shareDialog.titleWithName', { name: selectedSim!.scenario_name }) : t('simResultsMenu.shareDialog.fallbackTitle'));
                      //removedâ€œ${selectedSim!.scenario_name}â€` : 'Share results');
                      setShareRole(selectedSim!.role);
                    }}
                    aria-label={t('simResultsMenu.header.manageAccessAria')}
                  >
                    <Icon d={ICON_FALLBACKS.Share} size={20} />
                  </IconButton>
                </span>
              </Tooltip>
            )}
            {isPage ? (
              <Button
                variant="outlined"
                size="small"
                onClick={handleBackToMap}
                startIcon={<Icon size={20} name="arrow-left" />}
                sx={(theme) => drawerSecondaryActionSx(theme)}
              >
                {t('simResultsMenu.header.backToMap')}
              </Button>
            ) : (
              <IconButton onClick={onClose} size="small">
                <Icon size={20} name="xmark" />
              </IconButton>
            )}
          </Box>
        </Box>
        <Box sx={{ flex: 1, overflowY: 'auto', pr: 0, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
          {/* Multi-simulation queue: the card below always shows the active run */}
          {runQueue.length > 0 && (
            <Box data-testid="sim-results.queue" sx={{ mb: 2, p: 1, bgcolor: 'background.paper', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.875rem' }}>
                  {t('simResultsMenu.queue.title', {
                    done: runQueue.filter(r => r.status === 'finished' || r.status === 'failed').length,
                    total: runQueue.length,
                  })}
                </Typography>
                {runQueue.some(r => r.status === 'queued') && (
                  <Button size="small" onClick={cancelQueuedRuns} sx={{ textTransform: 'none' }}>
                    {t('simResultsMenu.queue.cancelRemaining')}
                  </Button>
                )}
              </Box>
              {runQueue.map(run => (
                <Box key={run.key} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, py: 0.25 }}>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>{run.name}</Typography>
                    {run.error && (
                      <Typography variant="caption" color="error" sx={{ display: 'block' }}>{run.error}</Typography>
                    )}
                  </Box>
                  <Chip
                    label={t(`simResultsMenu.queue.status.${run.status}`)}
                    size="small"
                    variant="outlined"
                    sx={{ height: 20, fontSize: '0.7rem' }}
                    color={
                      run.status === 'running' ? 'primary' :
                      run.status === 'finished' ? 'success' :
                      run.status === 'failed' ? 'error' : 'default'
                    }
                  />
                </Box>
              ))}
            </Box>
          )}

          {/* Current Simulation Info Card */}
          {status !== 'idle' && (
            <Box sx={{ mb: 2, p: 1, bgcolor: 'background.paper', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', fontSize: '0.875rem' }}>
                <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.875rem', whiteSpace: 'nowrap' }}>
                  {t('simResultsMenu.currentSim.label')}
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.875rem', whiteSpace: 'nowrap' }}>
                  {simulationName || t('simResultsMenu.unnamedSimulation')}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem', whiteSpace: 'nowrap' }}>{t('simResultsMenu.currentSim.idLabel')}</Typography>
                <Typography variant="body2" sx={{ fontWeight: 500, fontFamily: 'monospace', fontSize: '0.75rem', whiteSpace: 'nowrap' }}>
                  {simulationId || t('simResultsMenu.currentSim.na')}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem', whiteSpace: 'nowrap' }}>{t('simResultsMenu.currentSim.vehiclesLabel')}</Typography>
                <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.875rem', whiteSpace: 'nowrap' }}>
                  {numberOfVehicles || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem', whiteSpace: 'nowrap' }}>{t('simResultsMenu.currentSim.ridesLabel')}</Typography>
                <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.875rem', whiteSpace: 'nowrap' }}>
                  {numberOfRides || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem', whiteSpace: 'nowrap' }}>{t('simResultsMenu.currentSim.presetLabel')}</Typography>
                <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.875rem', whiteSpace: 'nowrap' }}>
                  {timeSettingsPreset || t('simResultsMenu.currentSim.presetDefault')}
                </Typography>
                <Chip 
                  label={status} 
                  size="small"
                  sx={{ height: 20, fontSize: '0.7rem' }}
                  color={
                    status === 'running' ? 'primary' :
                    status === 'finished' ? 'success' :
                    status === 'failed' ? 'error' : 'default'
                  }
                  variant="outlined"
                />
              </Box>
            </Box>
          )}

          {/* Step Tracker Panel */}
          <Box sx={{ mb: 2 }}>
            <Typography fontWeight={600} sx={{ mb: 1 }}>
              {t('simResultsMenu.progressHeading')}
            </Typography>
            <SimulationStepTrackerPanel
              title=""
              stepStatuses={stepStatuses ? filterOutScenarioStep(stepStatuses) : ['notStarted','notStarted','notStarted','notStarted','notStarted']}
            />
          </Box>

          {simulationError && (
            <Box sx={{ mb: 2 }}>
              <Alert severity="error">
                <Typography variant="subtitle1" fontWeight={600}>
                  {simulationError.structuredError.what}
                </Typography>
                <Typography variant="body2" sx={{ mt: 1 }}>
                  <strong>{t('simResultsMenu.errorAlert.whyLabel')}</strong> {simulationError.structuredError.why}
                </Typography>
                {simulationError.structuredError.howToFix && (
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    <strong>{t('simResultsMenu.errorAlert.howToFixLabel')}</strong> {simulationError.structuredError.howToFix}
                  </Typography>
                )}
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<Icon size={20} name="file-arrow-down" />}
                  onClick={() => {
                    const inputs = simulationError.structuredError.details?.simulationInputs;
                    const payload = inputs
                      ? { error: { what: simulationError.structuredError.what, why: simulationError.structuredError.why, howToFix: simulationError.structuredError.howToFix }, ...inputs }
                      : { error: { what: simulationError.structuredError.what, why: simulationError.structuredError.why, howToFix: simulationError.structuredError.howToFix } };
                    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = `simulation-debug-${new Date().toISOString().slice(0, 10)}.json`;
                    a.click();
                    URL.revokeObjectURL(url);
                  }}
                  sx={(theme) => ({
                    mt: 1,
                    ...drawerSecondaryActionSx(theme),
                  })}
                  title={t('simResultsMenu.errorAlert.downloadDebugTooltip')}
                >
                  {t('simResultsMenu.errorAlert.downloadDebug')}
                </Button>
              </Alert>
            </Box>
          )}

          {/* Past Results + Insights */}
          {!isPage && (
            <Box sx={{ mb: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box
                sx={{
                  p: 1.5,
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1.5,
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  alignItems: 'center',
                  justifyContent: 'center',
                  textAlign: 'center',
                  gap: 1.25,
                }}
              >
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                    {t('simResultsMenu.pastResults.title')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t('simResultsMenu.pastResults.description')}
                  </Typography>
                </Box>
                <Button
                  variant="outlined"
                  size="medium"
                  onClick={() => navigate('/simulation-results')}
                  startIcon={<Icon size={20} d={ICON_FALLBACKS.Timeline} />}
                  sx={(theme) => ({
                    ...drawerSecondaryActionSx(theme),
                    px: 3,
                    py: 1,
                    fontSize: '0.95rem',
                    minHeight: 44,
                    flexShrink: 0,
                    ...(isResultsReady
                      ? {
                          color: opsColors.semantic.success.color,
                          borderColor: opsColors.semantic.success.color,
                          backgroundColor: alpha(opsColors.semantic.success.color, 0.14),
                          animation: `${readyButtonPulse} 1.4s ease-in-out infinite`,
                          '&:hover': {
                            backgroundColor: alpha(opsColors.semantic.success.color, 0.2),
                            borderColor: opsColors.semantic.success.color,
                          },
                        }
                      : {}),
                  })}
                >
                  {t('simResultsMenu.pastResults.openButton')}
                </Button>
              </Box>

              <Box
                sx={{
                  width: '100%',
                  maxWidth: 640,
                  mx: 'auto',
                }}
              >
                <LiftangoInsightsCarousel enabled={open && !isPage} />
              </Box>
            </Box>
          )}

          {isPage && (
          <>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
            <Typography variant="h5" fontWeight={700} gutterBottom display="flex" alignItems="center">
              <Icon size={28} d={ICON_FALLBACKS.Timeline} style={{ marginRight: 8, color: '#ef7335' }} /> {t('simResultsMenu.title')}
            </Typography>
          </Box>
          <Box display="flex" flexDirection={{ xs: 'column', md: 'row' }} gap={2} mb={2}>
          <Box display="flex" alignItems="center" gap={1} flex={1}>
            <Autocomplete
              fullWidth
              options={simulationOptionsForSelect}
              value={simulationOptionsForSelect.find(sim => sim.run_id === selectedRunId) ?? null}
              loading={loadingResults}
              disabled={loadingResults}
              onChange={(_, sim) => setSelectedRunId(sim?.run_id ?? '')}
              getOptionLabel={(sim) => sim?.scenario_name ? `${sim.scenario_name} (${sim.run_id})` : sim?.run_id ?? ''}
              isOptionEqualToValue={(option, value) => option.run_id === value.run_id}
              filterOptions={(options, state) => {
                const query = state.inputValue.trim().toLowerCase();
                if (!query) return options;
                return options.filter((sim) =>
                  sim.run_id.toLowerCase().includes(query) ||
                  (sim.scenario_name ?? '').toLowerCase().includes(query)
                );
              }}
              noOptionsText={
                resultsError
                  ? resultsError
                  : loadingResults
                    ? t('simResultsMenu.pageView.loadingResults')
                    : t('simResultsMenu.pageView.noSimulations')
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t('simResultsMenu.search.label')}
                  placeholder={t('simResultsMenu.search.placeholder')}
                  sx={{
                    '& .MuiInputLabel-root': {
                      color: 'text.secondary',
                    },
                    '& .MuiInputLabel-root.Mui-focused': {
                      color: 'text.primary',
                    },
                    '& .MuiInputBase-input': {
                      color: 'text.primary',
                    },
                    '& .MuiInputBase-input::placeholder': {
                      color: 'text.secondary',
                      opacity: 0.95,
                    },
                    '& .MuiAutocomplete-popupIndicator, & .MuiAutocomplete-clearIndicator': {
                      color: 'text.secondary',
                    },
                    '& .MuiOutlinedInput-root': {
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'divider',
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'text.secondary',
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'actionBrand.primary',
                      },
                    },
                  }}
                />
              )}
              renderOption={(props, sim) => {
                const success = typeof sim.success_ratio === 'number' ? sim.success_ratio : '--';
                const vehicles = sim.no_vehicles ?? '--';
                const runTime = sim.run_time ? new Date(sim.run_time).toLocaleString() : '--';

                return (
                  <Box component="li" {...props} sx={{ alignItems: 'flex-start' }}>
                    <Box display="flex" flexDirection="column" alignItems="flex-start">
                      <Box>
                        <b>{sim.scenario_name || sim.run_id}</b>{sim.scenario_name && <span style={{ color: 'inherit', opacity: 0.6, fontSize: '0.85em', marginLeft: 6 }}>{sim.run_id}</span>}
                      </Box>
                      <Box fontSize={12} color="text.secondary" display="flex" gap={3} flexWrap="wrap">
                        <span>{t('simResultsMenu.pageView.optionRides', { value: sim.no_rides ?? sim.num_requests ?? '--' })}</span>
                        <span>{t('simResultsMenu.pageView.optionVehicles', { value: vehicles })}</span>
                        <span>{t('simResultsMenu.pageView.optionSuccess', { value: typeof success === 'number' ? (success * 100).toFixed(1) + '%' : success })}</span>
                        <span>{t('simResultsMenu.pageView.optionRunTime', { value: runTime })}</span>
                      </Box>
                    </Box>
                  </Box>
                );
              }}
            />
            <IconButton
              onClick={handleReload}
              disabled={loadingResults}
              color="primary"
              title={t('simResultsMenu.reloadResultsTooltip')}
              sx={{ minWidth: 48, height: 48 }}
              data-testid="sim-results.action.reload"
            >
              {loadingResults ? <CircularProgress size={24} /> : <Icon name="arrows-rotate" />}
            </IconButton>
          </Box>
          <Box display="flex" gap={1} width={{ xs: '100%', md: '50%' }}>
            <TextField
              label={t('simResultsMenu.enterRunId')}
              value={manualRunId}
              onChange={e => setManualRunId(e.target.value)}
              fullWidth
            />
            <Button
              variant="contained"
              onClick={handleManualLoad}
              disabled={!manualRunId.trim() || manualLoading}
              sx={(theme) => ({
                minWidth: 120,
                ...drawerPrimaryActionSx(theme),
              })}
              data-testid="sim-results.action.load-run-id"
            >
              {manualLoading ? t('simResultsMenu.pageView.loading') : t('simResultsMenu.pageView.load')}
            </Button>
          </Box>
          </Box>
          {manualError && (
            <Typography color="error" sx={{ mb: 2 }}>
              {manualError}
            </Typography>
          )}
          {!simulationHistory.length && (
            <Typography color="textSecondary" sx={{ mb: 2 }}>
              {t('simResultsMenu.pageView.runASimulationHint')}
            </Typography>
          )}
          {selectedSim && (
            <Box>
            <Box mb={2}>
              <Typography variant="h6" fontWeight={700} color="primary.main" gutterBottom sx={{ wordBreak: 'break-word', fontSize: 20 }}>
                {selectedSim.scenario_name}
              </Typography>
              <Typography variant="body2" color="textSecondary" gutterBottom sx={{ mb: 2 }}>
                {t('simResultsMenu.pageView.runTimeLabel', { time: selectedSim.run_time })}
              </Typography>
              <Box
                display="flex"
                alignItems="center"
                gap={1}
                sx={{ mb: 2, flexWrap: 'wrap' }}
              >
                <Typography variant="body2" color="textSecondary">
                  {t('simResultsMenu.pageView.resultsIdLabel', { id: selectedSim.results_id ?? t('simResultsMenu.pageView.resultsIdUnavailable') })}
                </Typography>
                {selectedSim.results_id && (
                  <QuickCopyButton
                    textToCopy={selectedSim.results_id}
                    ariaLabel={t('simResultsMenu.tripPanel.copyResultsIdAria')}
                    tone="onLight"
                  />
                )}
              </Box>
              
              {loadingDetailedData && (
                <Box display="flex" alignItems="center" gap={1} sx={{ mb: 2 }}>
                  <CircularProgress size={16} />
                  <Typography variant="body2" color="text.secondary">
                    {t('simResultsMenu.pageView.loadingDetailedData')}
                  </Typography>
                </Box>
              )}
              
              {detailedDataError && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  <Typography variant="body2">
                    {detailedDataError}
                  </Typography>
                </Alert>
              )}
            </Box>
            {/* Map, Chart, and Request Summary */}
            <Box mb={2} display="flex" flexDirection={{ xs: 'column', md: 'row' }} justifyContent="center" alignItems="flex-start" gap={2}>
              {/* Add to Map button on the left */}
              <Box>
                <IconButton
                  onClick={handleAddToMap}
                  color="primary"
                  size="large"
                  sx={{ mr: 1, mb: { xs: 1, md: 0 } }}
                  title={t('simResultsMenu.addToMapTooltip')}
                  disabled={!ridePoints || ridePoints.length === 0 || isAddingOnDemandToMap}
                  data-testid="sim-results.action.add-to-map"
                >
                  {isAddingOnDemandToMap ? <CircularProgress size={28} /> : <Icon d={ICON_FALLBACKS.AddLocationAlt} size={32} />}
                </IconButton>
                <Typography variant="caption" color="textSecondary" sx={{ display: 'block', textAlign: 'left', mt: 0.5 }}>
                  {t('simResultsMenu.pageView.addToMap')}
                </Typography>
              </Box>
              <Box>
                <Button
                  variant="contained"
                  color="secondary"
                  onClick={() => setImportFixedRoutesConfirmOpen(true)}
                  disabled={isImportingFixedRoutes || !(selectedSim?.activities && selectedSim.activities.length > 0)}
                  sx={{ mt: 0.5, whiteSpace: 'nowrap' }}
                >
                  {isImportingFixedRoutes ? t('simResultsMenu.pageView.importing') : t('simResultsMenu.pageView.importAsFixedRoute')}
                </Button>
                <Typography variant="caption" color="textSecondary" sx={{ display: 'block', textAlign: 'left', mt: 0.5, maxWidth: 200 }}>
                  {t('simResultsMenu.pageView.importAsFixedRouteHelper')}
                </Typography>
              </Box>
              {/* Map */}
              <Paper elevation={2} sx={{ width: 420, height: 220, overflow: 'hidden', borderRadius: 2 }}>
                {ridesError ? (
                  <Typography color="error" sx={{ p: 2 }}>{ridesError}</Typography>
                ) : ridePoints && ridePoints.length > 0 ? (
                  <MapContainer
                    preferCanvas
                    style={{ width: '100%', height: '100%' }}
                    center={[ridePoints[0].from_latitude, ridePoints[0].from_longitude]}
                    zoom={12}
                    scrollWheelZoom={false}
                    dragging={false}
                    doubleClickZoom={false}
                    zoomControl={false}
                    keyboard={false}
                    touchZoom={false}
                    boxZoom={false}
                  >
                    {googleBasemap.apiKey ? <GoogleTileLayer apiKey={googleBasemap.apiKey} mapType="roadmap" /> : <TileLayer
                      url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                      attribution="© OpenStreetMap contributors"
                    />}
                    <FitBounds points={ridePoints} fitKey={selectedSim?.run_id ?? 'default'} />
                    {/* Draw lines and markers for each ride */}
                    {ridePoints.map((p, i) => {
                      const from: [number, number] | undefined =
                        typeof p.from_latitude === 'number' && typeof p.from_longitude === 'number'
                          ? [p.from_latitude, p.from_longitude]
                          : undefined;
                      const to: [number, number] | undefined =
                        typeof p.to_latitude === 'number' && typeof p.to_longitude === 'number'
                          ? [p.to_latitude, p.to_longitude]
                          : undefined;
                      return (
                        <React.Fragment key={p.ride_id}>
                          {from && to && (
                            <Polyline positions={[from, to]} interactive={false} pathOptions={{ color: '#1976d2', weight: 2, opacity: 0.7 }} />
                          )}
                          {from && (
                            <CircleMarker center={from} radius={5} bubblingMouseEvents={false} pathOptions={RIDE_PICKUP_DOT}>
                              <Popup className="sim-map-point-popup sim-map-point-popup--top" autoPan={false}>
                                <MapPointPopupContent
                                  mode="ride_point"
                                  leg="pickup"
                                  activity={null}
                                  ridePoint={p as Record<string, unknown>}
                                  activities={selectedSim?.activities}
                                  vehicleKeyToLabel={vehicleKeyToLabel}
                                />
                              </Popup>
                            </CircleMarker>
                          )}
                          {to && (
                            <CircleMarker center={to} radius={5} bubblingMouseEvents={false} pathOptions={RIDE_DROPOFF_DOT}>
                              <Popup className="sim-map-point-popup sim-map-point-popup--top" autoPan={false}>
                                <MapPointPopupContent
                                  mode="ride_point"
                                  leg="dropoff"
                                  activity={null}
                                  ridePoint={p as Record<string, unknown>}
                                  activities={selectedSim?.activities}
                                  vehicleKeyToLabel={vehicleKeyToLabel}
                                />
                              </Popup>
                            </CircleMarker>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </MapContainer>
                ) : (
                  <Box display="flex" alignItems="center" justifyContent="center" height={220}>
                    <Typography color="textSecondary">{t('simResultsMenu.noRidePoints')}</Typography>
                  </Box>
                )}
              </Paper>
              {/* Chart next to map */}
              <TripsPerHourChart data={tripsPerHourData} />
              
              {/* Request Summary Cards */}
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, minWidth: 200, height: 220, justifyContent: 'flex-start' }}>
                <Paper
                  elevation={2}
                  sx={{
                    p: 1.2,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 0.5,
                    borderRadius: 2,
                    background: 'rgba(255,255,255,0.96)',
                    height: 'calc((100% - 3rem) / 3)',
                    boxShadow: '0 2px 8px 0 rgba(0,0,0,0.06)',
                    transition: 'box-shadow 0.2s',
                    '&:hover': { boxShadow: '0 4px 16px 0 rgba(0,0,0,0.10)' },
                  }}
                >
                  <Box fontSize={22} color="primary.main">
                    <Icon size={20} name="rectangle-list" />
                  </Box>
                  <Typography variant="h6" fontWeight={700} color="primary.main" sx={{ fontSize: 18, lineHeight: 1 }}>
                    {selectedSim.no_rides ?? t('simResultsMenu.pageView.naShort')}
                  </Typography>
                  <Typography variant="caption" color="textSecondary" fontWeight={500} textAlign="center" sx={{ fontSize: 12, lineHeight: 1.1 }}>
                    {t('simResultsMenu.pageView.totalRequests')}
                  </Typography>
                </Paper>
                
                <Paper
                  elevation={2}
                  sx={{
                    p: 1.2,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 0.5,
                    borderRadius: 2,
                    background: 'rgba(255,255,255,0.96)',
                    height: 'calc((100% - 3rem) / 3)',
                    boxShadow: '0 2px 8px 0 rgba(0,0,0,0.06)',
                    transition: 'box-shadow 0.2s',
                    '&:hover': { boxShadow: '0 4px 16px 0 rgba(0,0,0,0.10)' },
                  }}
                >
                  <Box fontSize={22} color="success.main">
                    <Icon size={20} name="circle-check" />
                  </Box>
                  <Typography variant="h6" fontWeight={700} color="success.main" sx={{ fontSize: 18, lineHeight: 1 }}>
                    {selectedSim.no_rides && typeof selectedSim.success_ratio === 'number'
                      ? Math.round(selectedSim.no_rides * selectedSim.success_ratio)
                      : t('simResultsMenu.pageView.naShort')}
                  </Typography>
                  <Typography variant="caption" color="textSecondary" fontWeight={500} textAlign="center" sx={{ fontSize: 12, lineHeight: 1.1 }}>
                    {t('simResultsMenu.pageView.successfulRequests')}
                  </Typography>
                </Paper>
                
                <Paper
                  elevation={2}
                  sx={{
                    p: 1.2,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 0.5,
                    borderRadius: 2,
                    background: 'rgba(255,255,255,0.96)',
                    height: 'calc((100% - 3rem) / 3)',
                    boxShadow: '0 2px 8px 0 rgba(0,0,0,0.06)',
                    transition: 'box-shadow 0.2s',
                    '&:hover': { boxShadow: '0 4px 16px 0 rgba(0,0,0,0.10)' },
                  }}
                >
                  <Box fontSize={22} color="success.main">
                    <Icon size={20} d={ICON_FALLBACKS.TrendingUp} />
                  </Box>
                  <Typography variant="h6" fontWeight={700} color="success.main" sx={{ fontSize: 18, lineHeight: 1 }}>
                    {typeof selectedSim.success_ratio === 'number'
                      ? (selectedSim.success_ratio * 100).toFixed(2) + '%'
                      : selectedSim.success_ratio ?? t('simResultsMenu.pageView.naShort')}
                  </Typography>
                  <Typography variant="caption" color="textSecondary" fontWeight={500} textAlign="center" sx={{ fontSize: 12, lineHeight: 1.1 }}>
                    {t('simResultsMenu.pageView.successRate')}
                  </Typography>
                </Paper>
              </Box>
            </Box>
            <Typography variant="subtitle1" fontWeight={700} mb={1} color="textSecondary">{t('simResultsMenu.keyMetrics')}</Typography>
            <MetricsGrid metrics={metrics} selectedSim={selectedSim} />
            
            {/* FRODO Report Section */}
            {selectedSim.frodoEnabled && (
              <Accordion sx={{ mt: 3 }}>
                <AccordionSummary expandIcon={<Icon size={20} name="chevron-down" />}>
                  <Typography variant="subtitle1" fontWeight={600}>
                    {t('simResultsMenu.pageView.frodoReportHeading')}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  {selectedSim.frodoReport ? (
                    <FrodoReportComponent
                      report={selectedSim.frodoReport}
                      offloadedRides={selectedSim.frodoOffloadedRides}
                      regularRidePoints={ridePoints || undefined}
                    />
                  ) : (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 2, bgcolor: 'info.light', borderRadius: 1 }}>
                      <Icon size={20} name="circle-info" />
                      <Typography variant="body2" color="text.secondary">
                        {t('simResultsMenu.pageView.frodoNoOffloads')}
                      </Typography>
                    </Box>
                  )}
                </AccordionDetails>
              </Accordion>
            )}
          </Box>
        )}
        {simulationHistory.length === 0 && (
          <Typography>{t('simResultsMenu.noResults')}</Typography>
        )}
          </>
          )}
        </Box>
      </Box>
  );

  if (isPage) {
    return (
      <Box sx={pageContainerSx}>
        {innerContent}
      </Box>
    );
  }
  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      variant="persistent"
      PaperProps={{ sx: drawerPaperSx }}
      data-testid="sim-results.drawer.container"
      aria-hidden={open ? 'false' : 'true'}
    >
      {innerContent}
      <ShareAccessDialog
        open={Boolean(shareResultsId)}
        title={shareResultsTitle || t('simResultsMenu.shareDialog.fallbackTitle')}
        currentUserEmail={currentUserEmail}
        canManage={shareRole === 'Owner'}
        listShares={() => listSimulationResultsShares(shareResultsId!)}
        share={(email, role) => shareSimulationResults(shareResultsId!, { email, role })}
        revoke={(email) => revokeSimulationResultsShare(shareResultsId!, email)}
        onClose={() => setShareResultsId(null)}
      />
    </Drawer>
  );
};

export { SimulationResultsMenu };
export default SimulationResultsMenu;
